/* =====================================================================
   THE ROOM ENGINE — desk-books.js: THE TWO BOOKS ON THE DESK

   C0 (2026-09-18). A desk piece: registered through `RoomObjects.piece`,
   drawn in the desk pass between the oak and the sheet, and the whole of
   what this desk knows about books. It was inside objects.js, where the
   sheet's vertex shader named `uBkA` and `uBkB` and the oak's fragment
   shader named `uBookA` and `uBookB`; neither shader knows a book from a
   biscuit tin now. This file tells them:

     RoomObjects.under('book-field',  ...)  the paper is draped over me
     RoomObjects.shadow('book-field', ...)  I stand on the oak, here

   and the two lists do the rest. Everything below — the covers' real
   millimetres, the hover lift, the physical opening, the reader
   substitution, the DOM labels' projective plate and the hit clip — is
   MOVED, not rewritten: the comments are the ones the cards R7, R15,
   R22, R29, R30, R45, R63, R67 and R80 wrote, and they carry Tommy's
   rulings with them.

   WHY IT IS A `<script defer>` AND NOT A FETCH. See the contract at the
   top of objects.js: the room's furniture may land late, the desk's may
   not — these two covers are in the first picture the reader ever sees.

   Switches kept: `?books=0` (no books at all), `?lbl=3d` / `?lbl=aff`
   (R30's label plate).
   ===================================================================== */
(function () {
'use strict';
var O = window.RoomObjects;
if (!O || !O.piece) return;

var Q = new URLSearchParams(location.search);
var NO_BOOKS = Q.get('books') === '0';
/* BV1 (2026-10-05): ON THE OPEN SIDEWAYS MAP THE REAL BOOKS LIE ON THE
   PAPER'S FOOT, one in each bottom corner of the glass, their heads peeking
   up from its edge (they were laid off the glass, PH10, with two DOM spine
   buttons in their place: "a little naff"). Tommy picked this from two
   variants (the other was the buttons restyled as cloth heads): "A2 is more
   consistent" -- the books are the same objects everywhere. */
var BK_A2 = true;
var A2_PEEK = 50;            /* css px of each cover above the glass's foot */
var A2_IN = 30;              /* and in from the glass's side */
var R7 = '#define R7 1\n';
/* The paper's own numbers — its float over the cloth, the run of its fall,
   R45's two radii — are NOT a book's: a sheet folded in eight has one
   stiffness whatever it is lying on. They stay with the sheet and reach
   this file as `ctx.paper` (objects.js, R15/R22/R45). */

/* the cover, in css pixels: .book-cover is 9.4rem x 6.4rem and the face
   is the bottom 6.4rem of it; .book-spine is .8rem (styles.css:1249) */
var BK_W = 150.4, BK_H = 102.4, BK_SPINE = 12.8;
/* how thick a shut field book is on the desk. The CSS had no thickness at
   all — it was a rectangle with a box-shadow — so this is the one number
   in E4 that is not read off the old look. A cloth-bound octavo of 200
   leaves is about 18 mm; at this desk's scale that is 15 css px. */
var BK_Z = 15;
var BOOK_SHOW = 64;          // survey-map.js:246

/* ==== R63 / R67 (a): AND THEY ARE REAL BOOKS, AND EACH ITS OWN SIZE ===
   Tommy: "i do also wonder if we should be making the closed books more
   life-sized? otherwise the physicality is not realistic" — and then, on
   R63's contact sheet: "we should think about the real size of each, the
   field guide is a smaller book, right? ... the field guide is also
   likely a thinner book? the journal is likely taller and wider".

   R63's arithmetic said by how much. Every other thing on this desk is
   struck in MILLIMETRES off the sheet's own width — the lamp (LAMP_MM),
   the laptop (LAP), the pencil, the floorboards all divide by 840 — and
   the two books were the only objects on it measured in raw css pixels.
   At the fit (1536 x 864 css) the sheet is 1417 css px wide, so a
   millimetre is 1.687 px, and BK_W/BK_H/BK_Z above come out as

     both books   89.1 x 60.7 x 8.9 mm, spine 7.6

   which is not a book. It is a matchbox with a label on it. Worse, in
   raw px it had no physical size at all: at 780 x 1410 the same numbers
   are 184 x 126 mm, so the covers GREW 2.1x as the window narrowed while
   the sheet, the lamp, the laptop and the pencil beside them did not.

   R67 ships two DIFFERENT books, in millimetres, and they are the
   DEFAULT:

     the field book   a surveyor's pocket book, 100 x 150 x 11 mm
     the journal      a bound volume of essays, 170 x 250 x 30 mm

   Everything placed BY a book follows from `bookDims` — the footprint,
   the show past the sheet's edge, the spine's overhang, the drape, the
   lamp's shadow, the label's own box and the lift — so the two are a
   different size everywhere on this desk and not only in their outline.

   (`?books=0` still means "no books at all", as it has since E4.) */
/* width (spine to fore-edge), depth (head to tail), thickness — mm.
   The spine's round back is kept at the proportion the present book has
   it at (12.8 css px of spine on 15 of thickness). */
var BK_REAL_MM = [[100, 150, 11], [170, 250, 30]];
var BK_SP_OF_Z = BK_SPINE / BK_Z;
var BKD = { k: '' };
/* The fallback is one frozen literal: bookDims is called four times a
   frame and must not allocate while layout is unavailable. */
var BK_DEF = [{ w: BK_W, h: BK_H, z: BK_Z, sp: BK_SPINE },
              { w: BK_W, h: BK_H, z: BK_Z, sp: BK_SPINE }];
function bookDims(S) {
  var L = S && S.layout, f = S && S.fld;
  if (!L || !f || !f.sheet) return BK_DEF;
  var mm = f.sheet.w * L.k / 840, key = mm.toFixed(5);
  if (BKD.k === key) return BKD.d;
  var d = [];
  for (var i = 0; i < 2; i++) {
    var r = BK_REAL_MM[i], w = r[0] * mm, h = r[1] * mm, z = r[2] * mm;
    d.push({ w: w, h: h, z: z, sp: z * BK_SP_OF_Z });
  }
  BKD.k = key; BKD.d = d;
  return d;
}

/* ==== R67 (c): AND WHAT HAPPENS TO AN OPEN BOOK WHEN YOU LEAVE ========
   Tommy: "we should think about what should happen to the open books if
   you zoom out to the room/laptop".

   WHAT HAPPENS TODAY, measured on poc/room at 1e5a772 with the journal
   open (`?journal=1`), REPORT-room-R67.md (c) has the pictures:
     · STANDING — drawBooks below hides the whole `.book` aside the moment
       the camera leaves the desk, which is E4's rule for the LABELS and
       takes the open panel with it by accident. `data-open` is still
       true, so the shut cover is still GONE from the desk (LIFT[2+i]):
       the room has no journal on it at all, on the desk or anywhere. Sit
       down and the panel is simply back, open at the same page.
     · AT THE LAPTOP — nothing happens. The panel stands wide open over
       the machine you have turned to, at its full size, and the shut
       cover is still gone from the desk behind it.
     · FOLDED — nothing happens. The panel stands open over the folded
       packet, and the cover is still gone.

   The chosen answer keeps the book open ON THE DESK: the panel hides while
   the reader is standing, turned to the machine or folding the sheet — it
   cannot be read from there — and what lies where the shut book lay is an
   OPEN book, two leaves and a gutter, its pages the paper's white. Sitting
   down brings the panel back on it.
   `Room.mapLive()` is exactly "the reader has left the paper" — standing,
   the laptop and the fold are the three things it already counts — so it
   is the whole test and there is no fourth state to keep. */
/* Tommy, 2026-09-15 01:50: "leaving an open book is cool". */
/* ==== BK1: AND THERE IS ONE PLACE, NOT TWO ===========================
   Tommy, after 62541ce: "I'm not a huge fan of what we've done with the
   journal and the field guide. … when you're in the map view, the book
   goes over the map … and then goes back away. We should literally
   probably mimic that in the room view as well. Move the open journal
   into the same space it would be in when you view it in the map view …
   that's the physical space it should occupy at the desk when open. It
   should go back to the same closed space … The field guide should be
   the same."

   WHAT R67 BUILT AND THIS CARD TAKES OUT. There were two open places for
   one book. In the map view the reader is the DOM panel, which at the fit
   (1536 x 864, dpr 1.75) stands at 571.8 x 433.5 mm centred on the
   sheet's own frame at (-120.0, -170.2); from the room the same journal
   was drawn as a 340 x 250 mm spread at a REAR READING STATION
   (BOOK_READ_MM, [-170,-430]) 260 mm further north, at its real size.
   Stand up from the map and the book you were reading was not the book on
   the desk — a different size, in a different place. That is the mismatch.

   THE ONE POSE IS THE PANEL'S OWN RECTANGLE, and it is derived, not
   declared: styles.css anchors `.book-body` at the shut cover's south-west
   corner and `.journal-body` at its south-east, and gives each a width
   (`min(44rem, …)`, `--jw`) — and at the rest layout a css pixel on the
   glass IS a css pixel on the desk's own plane, which is the premise R17
   and R30's label plate are built on. So ONE measured number, the panel's
   width, plus the book's own leaves, is the whole pose:

     w = the panel's width          h = w * (depth / two leaves' width)

   The height is struck from the book and not from the panel, so the
   individual leaf keeps its exact 100:150 / 170:250 proportion whatever
   the window does; the south edge is the cover's own south edge, which is
   why a book grows northward out of its shut self and puts itself back
   into exactly the same closed space. At this scale the panel is 1.68
   times the shut book: an open book at reading size, which is what the
   map view has always been. There is no second number anywhere. */
var OPEN_Z = 0.5;
/* AND IT IS LYING ON THE MAP, so it stands off the paper and not off the
   oak. 4 mm: the flat sheet's own lie — the wave, the rim and the three
   crease ridges of SHEET_VS — reaches 2.47 mm at its highest point
   ANYWHERE on the sheet (recomputed in scripts/test-book-physical.js from
   the shader's own arithmetic), so this is a book set down on paper that
   is not quite flat, with 1.5 mm to spare. It was BOOK_READ_LIFT_MM when
   there was a station to lift it to. */
var BOOK_ON_PAPER_MM = 4;
/* The stored cover ceases to support the map as the hand takes it out. The
   open spread is laid ON the map at its reading pose, not left underneath
   tenting the paper from its old storage footprint. */
function bkGone(i) { return LIFT[2 + i].v; }
/* =================================================================
   THE BOOKS — two shut covers
   ================================================================= */
/* ==== R7: A BOUND BOOK, NOT A BOX ==================================

   E4's book is five quads of a unit cube with a colour per face: from
   above it is a rectangle of cloth, and from the room pose it is a
   rectangle of cloth with three grey strips. What a shut octavo actually
   shows, and what is built here:

     · THE BOARDS OVERHANG THE BLOCK. The squares — 2.2 css px here — are
       why a book has a shadow line round its own leaves and why the
       fore-edge is set BACK under the cover instead of flush with it.
       The recess is closed top and bottom by the boards' undersides, so
       a grazing eye looks into it and not through it.
     · THE BOARD HAS A THICKNESS AND A BEVEL. 1.8 css px of board, with a
       1.3 x 0.9 chamfer round the three fore edges. The bevel is a face
       at about forty degrees: under the sun it is a hairline brighter
       than the cover and at night it is the one thing on the desk the
       lamp catches side-on.
     · THE SPINE IS ROUND. A half-ellipse from the top board's joint to
       the bottom board's, bulging out past the block — ten segments,
       with the cords raised across it — and the cloth is continuous
       over it, which is the silhouette that says "book" at any tilt.
     · THE LEAVES RUN THE RIGHT WAY. E4 striped the fore-edge along its
       LENGTH; leaves are stacked through the thickness, so the lines run
       across it. They are 12 css px of block here, so what is drawn is
       a soft comb with a wander in it and not two hundred leaves.

   Every vertex carries a NORMAL, computed in css pixels in the builder.
   The box is scaled by (w, h, thickness)/k, and k is the same in all
   three, so a normal taken in css is still the normal in sheet units —
   which is what lets one static buffer serve both books at any zoom.
   ==================================================================== */
var BOOK_VS = `#version 300 es
precision highp float;
` + R7 + `
#ifdef R7
layout(location = 0) in vec4 a;            // unit box xyz, and what it is
layout(location = 1) in vec3 nm;           // its normal, in css px
layout(location = 2) in float movingLeaf;  // the board that turns about the spine
out vec3 vP; out vec3 vN; out vec3 vW; out float vK;
uniform float uFlip;                        // the spine at the east
uniform float uOpen;                        // 0 shut, 1 two leaves laid flat
#else
layout(location = 0) in vec4 a;            // unit box, and which face
out vec3 vP; out float vFace;
#endif
uniform vec4 uBox;                          // sheet units: x y w h
uniform float uZ;                           // thickness, sheet units
uniform float uZ0;                          // R22: and how far it has been picked up
uniform vec3 uSheet; uniform mat4 uVP;
void main(){
#ifdef R7
  vec3 q = a.xyz, n = nm;
  vec3 p = vec3(q.x*uBox.z, q.y*uBox.w, q.z*uZ);
  if (movingLeaf > 0.5) {
    /* Rotate after scaling: a 250 mm journal must not inherit the hinge
       angle of a unit cube. Open it is the west leaf; shut it stacks over
       the fixed half from half-height to the cover's full height. */
    float th = 3.14159265359*(1.0 - uOpen), c = cos(th), s = sin(th);
    float rx = p.x - 0.5*uBox.z, rz = p.z - 0.5*uZ;
    p.x = 0.5*uBox.z + c*rx + s*rz;
    p.z = 0.5*uZ - s*rx + c*rz;
    n = vec3(c*n.x + s*n.z, n.y, -s*n.x + c*n.z);
  }
  if (uFlip > 0.5) { p.x = uBox.z - p.x; n.x = -n.x; }
  p.xy += uBox.xy; p.z += uZ0;
  vP = a.xyz; vN = normalize(n); vK = a.w; vW = p;
#else
  vec3 p = vec3(uBox.xy + a.xy*uBox.zw, a.z*uZ);
  vP = vec3(a.xy, a.z);
  vFace = a.w;
#endif
  gl_Position = uVP * vec4(uSheet.x + p.x*uSheet.z, -(uSheet.y + p.y*uSheet.z), p.z*uSheet.z, 1.0);
}`;

var BOOK_FS = `#version 300 es
precision highp float;
` + R7 + `
#ifdef R7
in vec3 vP; in vec3 vN; in vec3 vW; in float vK;
uniform vec3 uEye; uniform float uSpec; uniform float uZpx;
#else
in vec3 vP; in float vFace;
#endif
out vec4 o;
uniform vec3 uC0, uC1, uC2;
uniform vec2 uSpine;       // spine width as a fraction of the cover, and which side
uniform float uKind;       // 0 buckram, 1 morocco
uniform vec3 uLight;
uniform vec2 uPx;          // the cover in device px, for the weave
#ifdef R7
uniform vec4 uPaper;       // R15: the sheet, in sheet units
uniform vec3 uPCurl;       // R15: the chamfer the curl is folded off, how dark the contact is,
                           // and R22: how high the fold's edge stands over the cloth
#endif
` + O.GLSL.COMMON + `
void main(){
  vec2 q = vP.xy;

  /* the cloth: linear-gradient(120deg, c0 0%, c1 55%, c2 100%) */
  float a = radians(120.0);
  vec2 dir = vec2(sin(a), -cos(a));
  float L = abs(uPx.x*sin(a)) + abs(uPx.y*cos(a));
  float t = clamp(dot((q - 0.5)*uPx, dir)/L + 0.5, 0.0, 1.0);
  vec3 col = t < 0.55 ? mix(uC0, uC1, t/0.55) : mix(uC1, uC2, (t-0.55)/0.45);

  if (uKind < 0.5) {
    /* buckram: a coarse weave, one device pixel each way */
    vec2 w = q*uPx;
    col *= 1.0 + (fract(w.y*0.5) < 0.5 ? 0.045 : -0.05);
    col *= 1.0 + (fract(w.x*0.5) < 0.5 ? 0.045 : -0.05);
    col *= 1.0 - 0.34*clamp(1.0 - rad(q, vec2(0.78,0.82), vec2(1.20,1.20))/0.58, 0.0, 1.0);
  } else {
    /* morocco: a fine grain, and the light falling off the other way */
    float g = fbm1(q*uPx*0.55 + 5.0);
    col *= 1.0 - clamp(0.38*g - 0.16, 0.0, 1.0);
    col *= 1.0 - 0.40*clamp(1.0 - rad(q, vec2(0.22,0.82), vec2(1.20,1.20))/0.58, 0.0, 1.0);
  }

#ifdef R7
  /* ---- R7 ---------------------------------------------------------- */
  /* R67 (c): THE PAGE. An open book's leaves are not the block's comb
     seen end-on — they are the paper itself, face up, and from across a
     room that is all they are: one white, a little fibre in it, and the
     gutter going dark where the leaves turn down into the sewing. The
     light is measured from flat like everything else on this desk, so the
     two quads that run down into the gutter shade themselves. */
  if (vK > 3.5) {
    col = vec3(0.930, 0.907, 0.845);
    col *= 1.0 - 0.055*vnoise(q*uPx*0.11);
    col *= 1.0 - 0.26*(1.0 - smoothstep(0.0, 0.075, abs(q.x - 0.5)));
  } else if (vK > 0.5 && vK < 1.5) {
    /* THE BLOCK. Leaves stacked through the thickness, so the comb runs
       in z; a slow wander along the length so it is a hand-cut edge and
       not a ruling; and the board's overhang shading the top of it. */
    float sZ = vP.z*uZpx;
    float band = abs(fract(sZ*0.62) - 0.5)*2.0;
    float wob = vnoise(vec2((q.x + q.y)*uPx.x*0.05, sZ*0.55));
    col = mix(vec3(0.918,0.882,0.780), vec3(0.741,0.686,0.557),
              clamp(0.30 + 0.52*band + 0.30*(wob - 0.5), 0.0, 1.0));
    col *= mix(0.58, 1.0, smoothstep(0.90, 0.62, vP.z));     // under the squares
  } else {
    /* the joint at the spine, and the cords across it */
    if (vK > 2.5) {
      float cord = abs(fract(q.y*4.0 + 0.5) - 0.5)*2.0;
      col *= 1.0 + 0.07*smoothstep(0.72, 1.0, cord);
      col *= 0.94;
    } else if (vK > 1.5) {
      col *= 0.88;                                            // cloth turned over the edge
    }
    /* the hinge groove, where the cloth is creased over the joint */
    float hj = abs(q.x - uSpine.x)*uPx.x;
    col *= 1.0 - 0.30*clamp(1.6 - hj*0.7, 0.0, 1.0);
  }
  /* THE LIGHT, MEASURED FROM FLAT, as the paper's is: a cover lying face
     up returns exactly the cloth's own colour and everything that is not
     flat is read against it. E4 had a constant per face. */
  float lam = clamp(dot(vN, uLight), 0.0, 1.0);
  col *= 1.0 + 0.78*(lam - clamp(uLight.z, 0.0, 1.0));
  vec3 V = normalize(uEye - vW);
  vec3 hv = normalize(uLight + V);
  col += uSpec*(vK > 0.5 && vK < 1.5 ? 0.35 : 1.0)*pow(clamp(dot(vN, hv), 0.0, 1.0), 14.0);
  /* ==== R15: AND THE SHEET IS LYING ON THIS ========================
     The paper rests on the cover (SHEET_VS, bookLift) and everything of
     the cover that is still visible is the part OUTSIDE the paper's own
     outline — the spine, the squares, the foot of the board. What tells
     the eye the sheet is on top and not beside is the line of shade the
     paper's edge keeps off the cloth just past it: the paper rides two
     units above the cloth, so there is no cast shadow worth the name and
     this is a CONTACT shadow, a few units wide, strongest at the edge.
     Taken from the paper's outline in sheet units — the same rectangle
     with the same chamfer the desk's own shadow is struck from. */
  if (uPCurl.y > 0.0) {
    vec2 d = max(uPaper.xy - vW.xy, vW.xy - (uPaper.xy + uPaper.zw));
    float ch = ((vW.x - (uPaper.x + uPaper.z - uPCurl.x)) + (vW.y - (uPaper.y + uPaper.w - uPCurl.x)))*0.70710678;
    float od = max(max(d.x, d.y), ch);
    col *= 1.0 - uPCurl.y*exp(-max(od, 0.0)/4.5);
    /* ==== R45: AND A CONTACT LINE IS NOT THE WHOLE OF IT ==============
       Tommy's eye on the field book: "no shadow of the sheet on the
       book's cover". R15's line is four sheet units wide — three css px,
       which at the fit is under two device pixels and reads as an edge
       on the paper rather than as a shadow on the cloth. Two things are
       added, and both of them are things that are actually happening:
         · the cloth beside the sheet has lost most of its SKY. A board is
           two thirds covered by paper and what is left of it is in a slot
           a couple of millimetres deep; that is an ambient loss over a
           good centimetre, not a line.
         · and the sheet's edge CASTS. It rides PAPER_FLOAT over the cloth
           and the sun rakes it, so the cast is that height thrown along
           the light — short at noon, a finger's breadth at dusk — and it
           swings with the sun like everything else on this desk. */
    col *= 1.0 - 1.0*0.19*exp(-max(od, 0.0)/21.0);
    vec2 spf = vW.xy + uLight.xy*(2.2/max(uLight.z, 0.20));
    vec2 df = max(uPaper.xy - spf, spf - (uPaper.xy + uPaper.zw));
    float chf = ((spf.x - (uPaper.x + uPaper.z - uPCurl.x)) + (spf.y - (uPaper.y + uPaper.w - uPCurl.x)))*0.70710678;
    col *= 1.0 - 1.0*0.20*(1.0 - smoothstep(-1.5, 3.0, max(max(df.x, df.y), chf)));
    /* ==== R22 (a): AND THE TURNED CORNER THROWS ONE ==================
       The dog-ear is folded back and what it uncovers is THIS BOARD: the
       journal's own cover lies open in the notch, with the sheet's edge
       standing a good many units above it. So inside the notch — and only
       there, everywhere else the sheet is flat on the cloth and R15's
       contact line is the truth — the cover takes the fold's real cast
       shadow: the paper's outline sampled back along the sun from the
       fold's own height. Without it the notch reads as a hole punched in
       the sheet and not as a corner turned up off a book. */
    if (ch > 0.0 && od > 0.0) {
      vec2 sp = vW.xy + uLight.xy*(uPCurl.z/max(uLight.z, 0.20));
      vec2 ds = max(uPaper.xy - sp, sp - (uPaper.xy + uPaper.zw));
      float chs = ((sp.x - (uPaper.x + uPaper.z - uPCurl.x)) + (sp.y - (uPaper.y + uPaper.w - uPCurl.x)))*0.70710678;
      col *= 1.0 - 0.30*(1.0 - smoothstep(-2.5, 2.5, max(max(ds.x, ds.y), chs)));
    }
  }
  o = vec4(col, 1.0);
#else
  /* the spine, at the west of the field book and the east of the journal */
  float sx = uSpine.y > 0.5 ? (1.0 - q.x) : q.x;
  if (sx < uSpine.x) {
    float u = sx/uSpine.x;
    col *= mix(0.58, 1.06, smoothstep(0.0, 0.6, u))*mix(1.0, 0.78, smoothstep(0.6, 1.0, u));
    if (u > 0.97) col *= 0.65;
  }
  /* inset 0 0 0 1px rgba(0,0,0,.4) */
  float b = min(min(q.x, 1.0-q.x)*uPx.x, min(q.y, 1.0-q.y)*uPx.y);
  col = mix(col*0.6, col, smoothstep(0.0, 1.2, b));

  /* THE FACES OF THE BLOCK. The cover is the cloth; the head and the fore
     side are the leaves the CSS drew as two 3px strips of cream
     (.book-face::before/::after), which in a box are faces and not strips.
     The spine side is cloth turned over the block. */
  float face = vFace;
  if (face > 3.5) {
    /* the cover, facing the room */
    col *= 0.90 + 0.26*clamp(uLight.z, 0.0, 1.0);
  } else {
    bool foreSide = (uSpine.y > 0.5) ? (face < 2.5 && face > 1.5) : (face > 2.5);
    bool leaves = (face < 1.5 && face > 0.5) || foreSide;
    if (leaves) {
      float s = (face < 1.5 ? q.x*uPx.x : q.y*uPx.y);
      col = mix(vec3(0.914,0.875,0.769), vec3(0.788,0.733,0.600), step(1.0, fract(s*0.5)*2.0));
      col *= 0.86;
    }
    /* a side is turned away from the sky, so it takes the light side on */
    vec2 nrm = face < 1.5 ? vec2(0.0, face < 0.5 ? -1.0 : 1.0)
                          : vec2(face < 2.5 ? -1.0 : 1.0, 0.0);
    col *= 0.52 + 0.42*clamp(dot(nrm, uLight.xy), 0.0, 1.0);
  }

  o = vec4(col, 1.0);
#endif
}`;
/* R7's: the boards, the squares, the block and the round spine. Local
   coordinates are 0..1 of (BK_W, BK_H, BK_Z); normals are worked out in
   CSS PIXELS, which is the same direction the sheet-unit box gives them
   because the three axes are scaled by the same k. Kinds: 0 cloth face,
   1 the block, 2 cloth turned over an edge, 3 the spine. */
/* R63: the squares, the boards and the bevels are a BINDER'S numbers —
   2.2 css px of square is about 1.3 mm of board past the block — so they
   are millimetres too and they scale with the cover. At the default dims
   every fraction below is the one R7 struck, to the bit. */
function buildBook(gl, dim) {
  var f = dim ? dim.w / BK_W : 1;              // R63: the binder's scale
  var W = dim ? dim.w : BK_W, H = dim ? dim.h : BK_H, Z = dim ? dim.z : BK_Z;
  var SQ = 2.2 * f, BOARD = 1.8 * f, BEV = 1.3 * f, BEVZ = 0.9 * f;
  var sp = (dim ? dim.sp : BK_SPINE) / W;      // the joint
  var sx = SQ / W, sy = SQ / H;                // the squares
  var bz = BOARD / Z, bvz = BEVZ / Z;
  var ex = BEV / W, ey = BEV / H;
  var v = [], nrm = [], idx = [], c = 0;
  function quad(k, p, n) {
    for (var i = 0; i < 4; i++) { v.push(p[i][0], p[i][1], p[i][2], k); nrm.push(n[i][0], n[i][1], n[i][2]); }
    idx.push(c, c + 1, c + 2, c, c + 2, c + 3); c += 4;
  }
  function q1(k, p0, p1, p2, p3, n) { quad(k, [p0, p1, p2, p3], [n, n, n, n]); }
  function nz(a) { var m = Math.hypot(a[0], a[1], a[2]) || 1; return [a[0] / m, a[1] / m, a[2] / m]; }

  /* --- the top board: its face, its bevel, its edge ---------------- */
  q1(0, [sp, ey, 1], [1 - ex, ey, 1], [1 - ex, 1 - ey, 1], [sp, 1 - ey, 1], [0, 0, 1]);
  /* the three fore edges, each as (inset top) -> (outline, bevel foot) ->
     (outline, board foot). The outward directions are in css px. */
  var sides = [
    { o: [0, -1], A: [[sp, ey], [1 - ex, ey]], B: [[sp, 0], [1, 0]] },
    { o: [1, 0], A: [[1 - ex, ey], [1 - ex, 1 - ey]], B: [[1, 0], [1, 1]] },
    { o: [0, 1], A: [[1 - ex, 1 - ey], [sp, 1 - ey]], B: [[1, 1], [sp, 1]] }
  ];
  sides.forEach(function (sd) {
    var o = sd.o, A0 = sd.A[0], A1 = sd.A[1], B0 = sd.B[0], B1 = sd.B[1];
    var nb = nz([o[0] * BEVZ, o[1] * BEVZ, BEV]);
    q1(2, [A0[0], A0[1], 1], [A1[0], A1[1], 1], [B1[0], B1[1], 1 - bvz], [B0[0], B0[1], 1 - bvz], nb);
    q1(2, [B0[0], B0[1], 1 - bvz], [B1[0], B1[1], 1 - bvz], [B1[0], B1[1], 1 - bz], [B0[0], B0[1], 1 - bz], [o[0], o[1], 0]);
    /* the underside of the overhang, closing the recess over the block */
    var I0 = [B0[0] - o[0] * sx, B0[1] - o[1] * sy], I1 = [B1[0] - o[0] * sx, B1[1] - o[1] * sy];
    q1(2, [B0[0], B0[1], 1 - bz], [B1[0], B1[1], 1 - bz], [I1[0], I1[1], 1 - bz], [I0[0], I0[1], 1 - bz], [0, 0, -1]);
    /* the bottom board: its edge, and the floor of the recess */
    q1(2, [B0[0], B0[1], bz], [B1[0], B1[1], bz], [B1[0], B1[1], 0], [B0[0], B0[1], 0], [o[0], o[1], 0]);
    q1(2, [I0[0], I0[1], bz], [I1[0], I1[1], bz], [B1[0], B1[1], bz], [B0[0], B0[1], bz], [0, 0, 1]);
  });

  /* --- the block ---------------------------------------------------- */
  var bx0 = sp, bx1 = 1 - sx, by0 = sy, by1 = 1 - sy, bz0 = bz, bz1 = 1 - bz;
  q1(1, [bx0, by0, bz0], [bx1, by0, bz0], [bx1, by0, bz1], [bx0, by0, bz1], [0, -1, 0]);   // head
  q1(1, [bx0, by1, bz1], [bx1, by1, bz1], [bx1, by1, bz0], [bx0, by1, bz0], [0, 1, 0]);    // foot
  q1(1, [bx1, by0, bz0], [bx1, by1, bz0], [bx1, by1, bz1], [bx1, by0, bz1], [1, 0, 0]);    // the fore-edge

  /* --- the spine: a half-ellipse from joint to joint, and its two ends */
  var NSP = 10, SW = (dim ? dim.sp : BK_SPINE), arc = [];
  for (var i = 0; i <= NSP; i++) {
    var th = Math.PI * i / NSP;
    arc.push({ x: sp * (1 - Math.sin(th)), z: 0.5 + 0.5 * Math.cos(th),
               n: nz([-0.5 * Z * Math.sin(th), 0, SW * Math.cos(th)]) });
  }
  for (i = 0; i < NSP; i++) {
    var a0 = arc[i], a1 = arc[i + 1];
    quad(3, [[a0.x, 0, a0.z], [a1.x, 0, a1.z], [a1.x, 1, a1.z], [a0.x, 1, a0.z]],
            [a0.n, a1.n, a1.n, a0.n]);
  }
  for (i = 0; i < NSP - 1; i++) {
    quad(3, [[arc[0].x, 0, arc[0].z], [arc[i + 1].x, 0, arc[i + 1].z], [arc[i + 2].x, 0, arc[i + 2].z], [arc[0].x, 0, arc[0].z]],
            [[0,-1,0],[0,-1,0],[0,-1,0],[0,-1,0]]);
    quad(3, [[arc[0].x, 1, arc[0].z], [arc[i + 2].x, 1, arc[i + 2].z], [arc[i + 1].x, 1, arc[i + 1].z], [arc[0].x, 1, arc[0].z]],
            [[0,1,0],[0,1,0],[0,1,0],[0,1,0]]);
  }

  /* ==== BK1: AND A BUILDER BINDS ITS OWN VERTEX ARRAY FIRST ===========
     THE ELEMENT BUFFER BINDING IS VERTEX ARRAY STATE. This builder used
     to bind its index buffer while the CALLER's vertex array was still
     bound — drawBooks builds the second book's mesh in the middle of the
     loop, with the first book's array bound — so the shut field book's
     array had its index buffer swapped for the open journal's 96 indices
     underneath it. Every later draw of that cover was refused
     (INVALID_OPERATION, "insufficient buffer size") and the field book
     simply went out of the picture: measured on this branch before the
     fix, opening the journal at the fit and shutting it again left
     18,068 changed pixels in the sheet's south-west corner — the cloth
     gone, its label and its shadow still on the oak (room-rev10/
     BK1-BEFORE-fit-afterclose-175.png, `Room.gl.getError()` 1282 from
     then on, `__roomErr` empty throughout). C0's OPEN 1 asked for
     getError in every spec for exactly this class of fault; this is one.
     The array is created and bound FIRST now, so every binding below is
     written into it and into nothing else. */
  var vao = gl.createVertexArray(); gl.bindVertexArray(vao);
  var vb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, vb);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(v), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 4, gl.FLOAT, false, 0, 0);
  var nb2 = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, nb2);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(nrm), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 0, 0);
  var ib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(idx), gl.STATIC_DRAW);
  gl.bindVertexArray(null);
  O.bookN = idx.length; O.vaoBook = vao;
  return { vao: vao, n: idx.length, vb: vb, nb: nb2, ib: ib };
}
/* ==== THE SAME BOOK, PHYSICALLY OPENING ===============================
   The shut cover is E4's five quads grown into a bound box (buildBook);
   this is that box split into its two sewn halves. Each half keeps the
   CLOSED COVER'S width: open, the footprint is therefore two covers wide.
   The east half is fixed and the west half carries `movingLeaf=1`; BOOK_VS
   rotates that half about the gutter. At uOpen=0 it is stacked over the
   fixed block, from half-height to full height. At uOpen=1 both halves lie
   from the desk to half-height. uFlip mirrors the action for the journal,
   whose spine is on the east.

   Four materials, all but one of them the cover's own: 0 the cloth of the
   boards, 2 the cloth turned over their edges, 1 the block's cut edge
   under the leaves, and 4 — the one thing a shut book cannot show — the
   PAGE. 16 quads, one static buffer, one draw. */
function buildOpenBook(gl, dim) {
  var W = dim.w, H = dim.h, Z = dim.z, f = W / BK_W;
  var SQ = 2.2 * f, BOARD = 1.8 * f;
  var zb = Math.min(0.30, BOARD / Z);          /* the board, in unit z */
  var top = OPEN_Z;                            /* each half-block's top */
  var hg = Math.min(0.0275, (dim.sp * 0.16) / (2 * W));
  var sx = SQ / (2 * W), sy = SQ / H;          /* full-spread units */
  var v = [], nrm = [], mov = [], idx = [], c = 0;
  function quad(k, p, n, moving) {
    for (var i = 0; i < 4; i++) {
      v.push(p[i][0], p[i][1], p[i][2], k);
      nrm.push(n[i][0], n[i][1], n[i][2]); mov.push(moving ? 1 : 0);
    }
    idx.push(c, c + 1, c + 2, c, c + 2, c + 3); c += 4;
  }
  function q1(k, p0, p1, p2, p3, n, moving) {
    quad(k, [p0, p1, p2, p3], [n, n, n, n], moving);
  }
  [[0, 0.5 - hg, -1, 1], [0.5 + hg, 1, 1, 0]].forEach(function (sd) {
    var x0 = sd[0], x1 = sd[1], out = sd[2], moving = sd[3];
    /* The moving underside becomes the visible front cover when shut. */
    q1(0, [x0, 0, 0], [x0, 1, 0], [x1, 1, 0], [x1, 0, 0], [0, 0, -1], moving);
    q1(0, [x0, 0, zb], [x1, 0, zb], [x1, 1, zb], [x0, 1, zb], [0, 0, 1], moving);
    var fe = out < 0 ? x0 : x1;                /* the fore-edge of this board */
    q1(2, [fe, 0, 0], [fe, 1, 0], [fe, 1, zb], [fe, 0, zb], [out, 0, 0], moving);
    q1(2, [x0, 0, 0], [x1, 0, 0], [x1, 0, zb], [x0, 0, zb], [0, -1, 0], moving);
    q1(2, [x0, 1, zb], [x1, 1, zb], [x1, 1, 0], [x0, 1, 0], [0, 1, 0], moving);
    /* the leaves on it, inside the squares on the three outer sides */
    var lx0 = out < 0 ? x0 + sx : x0, lx1 = out < 0 ? x1 : x1 - sx;
    q1(4, [lx0, sy, top], [lx1, sy, top], [lx1, 1 - sy, top], [lx0, 1 - sy, top], [0, 0, 1], moving);
    var lf = out < 0 ? lx0 : lx1;
    q1(1, [lf, sy, zb], [lf, 1 - sy, zb], [lf, 1 - sy, top], [lf, sy, top], [out, 0, 0], moving);
    q1(1, [lx0, sy, zb], [lx1, sy, zb], [lx1, sy, top], [lx0, sy, top], [0, -1, 0], moving);
    q1(1, [lx0, 1 - sy, top], [lx1, 1 - sy, top], [lx1, 1 - sy, zb], [lx0, 1 - sy, zb], [0, 1, 0], moving);
  });
  /* the gutter: the leaves turn down into the sewing and come up the
     other side, so it is two ruled quads and the light does the rest */
  var gz = zb + (top - zb) * 0.62, gn = Math.hypot(hg, top - gz);
  q1(4, [0.5 - hg, sy, top], [0.5, sy, gz], [0.5, 1 - sy, gz], [0.5 - hg, 1 - sy, top],
     [(top - gz) / gn, 0, hg / gn], 1);
  q1(4, [0.5, sy, gz], [0.5 + hg, sy, top], [0.5 + hg, 1 - sy, top], [0.5, 1 - sy, gz],
     [-(top - gz) / gn, 0, hg / gn], 0);

  /* BK1: its own vertex array first, and every binding inside it — see
     buildBook above for the fault this was (the other book's index buffer
     swapped underneath it mid-loop). */
  var vao = gl.createVertexArray(); gl.bindVertexArray(vao);
  var vb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, vb);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(v), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 4, gl.FLOAT, false, 0, 0);
  var nb2 = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, nb2);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(nrm), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(1);
  gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 0, 0);
  var mb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, mb);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(mov), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(2);
  gl.vertexAttribPointer(2, 1, gl.FLOAT, false, 0, 0);
  var ib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array(idx), gl.STATIC_DRAW);
  gl.bindVertexArray(null);
  return { vao: vao, n: idx.length, vb: vb, nb: nb2, mb: mb, ib: ib };
}
/* One cache per book. Normal use still has one open at a time, but reversing
   one while opening the other must not rebuild two differently-sized meshes
   on alternate frames. */
O.openVA = [null, null];
function openMesh(gl, i, dim) {
  var k = dim.w.toFixed(3) + ',' + dim.h.toFixed(3) + ',' + dim.z.toFixed(3);
  var c = O.openVA[i];
  if (c && c.k === k) return c;
  if (c) { gl.deleteVertexArray(c.vao); gl.deleteBuffer(c.vb); gl.deleteBuffer(c.nb); gl.deleteBuffer(c.mb); gl.deleteBuffer(c.ib); }
  var m = buildOpenBook(gl, dim);
  m.k = k; O.openVA[i] = m;
  return m;
}

/* R63: ONE MESH PER COVER, and only when the cover's own shape changes.
   The two books are the same box at the default and the variants give
   them different ones; the fan is a static buffer either way, so this is
   two buffers instead of one and not a draw more. The key is the dims, so
   a pan, a zoom, a hover and a fold all reuse it and a RESIZE (which is
   what moves a millimetre) rebuilds. */
O.bookVA = [null, null];
function bookMesh(gl, i, dim) {
  var k = dim.w.toFixed(3) + ',' + dim.h.toFixed(3) + ',' + dim.z.toFixed(3) + ',' + dim.sp.toFixed(3);
  var c = O.bookVA[i];
  if (c && c.k === k) return c;
  /* and the two covers SHARE one fan when they are the same box, which is
     what the default is: one static buffer on this desk, as R7 had it */
  var o = O.bookVA[1 - i];
  if (o && o.k === k) { O.bookVA[i] = o; return o; }
  if (c && c !== o) { gl.deleteVertexArray(c.vao); gl.deleteBuffer(c.vb); gl.deleteBuffer(c.nb); gl.deleteBuffer(c.ib); }
  var m = buildBook(gl, dim);
  m.k = k; O.bookVA[i] = m;
  return m;
}
/* ==== R22: THE BOOK IS PICKED UP A LITTLE ============================
   Tommy: "when you hover a book instead of the name moving vertically,
   can't the whole book maybe pop over the paper somehow? what would look
   not crap?" What was happening was that the paper LABEL slid thirteen
   pixels up the cover and nothing else moved — the cloth, the spine, the
   shadow and the sheet lying over the head of the book all stayed exactly
   where they were, so it read as a caption on a hover state and not as a
   book on a desk.

   What happens now is that the BOOK moves. It slides out from under the
   sheet's corner toward its own outer corner — ten css px west and ten
   south for the field book, ten east and ten south for the journal, which
   is fourteen along the diagonal — and rises 1.8 px, about two
   millimetres at this desk's scale (the shut octavo is fifteen px for
   eighteen). Three things follow for nothing, because they are all
   functions of the same rectangle:

     · THE SHEET'S DRAPE follows the footprint. The lift over a board is
       computed from uBkA/uBkB in the vertex shader, so the tent walks
       with the book and the paper settles back over the hole it leaves.
       No mesh is rebuilt and no tenting is done on the main thread — the
       whole cost of a hover frame is the frame.
     · THE SHADOW ON THE DESK lengthens and softens with the rise
       (uBookL), because a thing that is further off the wood throws
       further.
     · THE LABEL RIDES THE BOOK, at exactly the same offset, written on
       .book-face — which is the element that used to do the thirteen
       pixels on its own.

   180 ms out on an ease-out cubic, 240 ms back on an ease-in-out: a book
   is picked up faster than it is put down. `prefers-reduced-motion` gets
   no movement at all, not a faster one. The BUTTON does not move — v14's
   rule, the hit box must not walk out from under the pointer — and
   nothing here touches how a click opens a book. */
/* R30: the label's plate — `?lbl=3d` for the exact trapezoid, `?lbl=aff`
   for the affine. Which is the default is a MEASUREMENT and section 2 of
   REPORT-room-R30.md is it. */
var LBL_3D = Q.get('lbl') === '3d';
/* 2026-09-19: `?hit=0` is the BEFORE picture — the cover alone takes the
   hand, as it did until the hit box (hitsAtPose). */
var HIT_ON = Q.get('hit') !== '0';
/* L: two answers to one question, and Tommy chose (2026-09-19, "the
   perspective version, I guess it is kind of cooler"): the landed panel in
   the map view stays on the block's own top face through the camera (a
   mild trapezoid, ~11 px taller and 26 px of skew at the journal's
   south-west corner, measured). `?leaf=flat` is the other answer, kept as
   a switch: the flat css rectangle the reader always had, with the block's
   top face showing past the panel's edge by that same residual. */
var LEAF_PERSP = Q.get('leaf') !== 'flat';
var BOOK_OUT = 10;           // css px out, on each axis
var BOOK_RISE = 1.8;         // css px of rise: two millimetres
var LIFT_OUT_MS = 180, LIFT_BACK_MS = 240;
/* four eased values: [0],[1] the two books' LIFT (picked up a little on
   hover), [2],[3] the two books' GONE (Tommy 2026-09-14: "the book in
   front is a replacement for the closed book behind … make the book
   underneath disappear to ensure it isn't visible") — while a book's panel
   is open the shut book is not drawn, and the paper's tent over it and its
   shadow on the desk ease away as it is lifted off the wood. */

/* =================================================================
   R29: A BOOK IS PUT DOWN THE WAY IT WAS PICKED UP

   Tommy: "there's something a little off about the way the books close,
   i'm not 100% sure what i mean though, open is fine but close is
   slightly odd. maybe the open book should close like it does into the
   closed book's hover state and then slot back in under the sheet?"

   What it did: the instant `data-open` went false the shut book came back
   — and came back HOME, seated under the sheet's corner with its tent and
   its shadow already on it — while the panel was still 94 % opaque and
   had another fifth of a second of collapsing to do. Two things then
   happened at once and the one that mattered happened under the other.
   Measured on the old build (styles.css, rAF sampling): GONE snapped 1 to
   0 at 11 ms; the leaves' opacity did not reach nought until 216 ms.

   What it does now is three movements in a row, and the reader sees them
   one at a time:

     0 ms   the panel starts to shut (books.js sets data-open false; the
            leaves fade over .22s and the block collapses over .3s).
            BEHIND them, at 94 % cover, the shut book is put back on the
            desk — not home, but in the HAND's pose: the hover lift, out
            by BOOK_OUT and up by BOOK_RISE, the shadow long. Nothing of
            this is visible; it is the substitution, and it is made under
            the only frame that can hide it.
     220 ms the leaves are gone (measured). What is on the desk is a book
            in exactly the state the pointer would have put it in.
     300 ms after a beat of LIFT_SETTLE it slots home: the hover's own
            240 ms ease-in-out, so the sheet's tent walks in with the
            footprint and the shadow shortens as the book arrives under
            the paper. The tent cannot rise over empty desk because the
            tent IS the book's footprint and the book is in it.

   And if the pointer is still on the cover when the panel shuts — which
   is what happens every time the book is closed by clicking its own
   spine — it stays in the hover state until the pointer leaves, and then
   takes the same 240 ms home. `over` cannot be trusted across the open
   (the cover has pointer-events:none while the panel is up, so no
   pointerleave is ever delivered for a hand that wandered off), so the
   beat ends with a hit test at the last known pointer position. */
/* L: the panel IS the book now (see LEAF below), so there is no reader to
   retract before the leaf shuts: the close begins on the frame Esc is
   pressed. The stage is kept — `retracting` is one tick long — so every
   path that watches the sequence sees the same sequence. */
var PANEL_SHUT_MS = 0;       /* was 220: styles.css .book-body opacity .22s, now gone */
var LIFT_SETTLE = 80;        /* the beat between the leaves going and the book slotting home */
var BOOK_HINGE_MS = 820;     /* slide, carry, then the 180-degree cover turn (SO1) */
/* ==== SO1 (2026-10-01): OUT FROM UNDER THE PAPER, THEN UP ==============
   Tommy: "is it not possible to make them slide out from under the paper
   before they come up and open? Currently, they kind of come through the
   paper." They did: the carry ran from the shut place straight to the
   reading place and rose as it went, so the block's head, which lies under
   the sheet's south edge, came up THROUGH the paper over it.

   So the one clock has three phases, overlapping a little so the hand
   never stops: the book SLIDES south on the oak until its head is clear of
   the paper's edge (the drape walks out with it, underOf), then it is
   LIFTED in a small arc and CARRIED over the map, then the cover TURNS.
   Run backwards the same clock closes it, sets it down south of the sheet
   and slides it home under the paper. At 820 ms the turn keeps about the
   410 ms it had at 520. */
var SO_SLIDE = [0, 0.3], SO_CARRY = [0.24, 0.52], SO_HINGE = 0.46;
var SO_CLEAR_MM = 3;         /* the head stops this far south of the paper's edge */
var SO_ARC_MM = 6;           /* how high the hand lifts it on the way across */
function bkSlide(v) { return bookEase(SO_SLIDE[0], SO_SLIDE[1], v); }
function bkCarry(v) { return bookEase(SO_CARRY[0], SO_CARRY[1], v); }
function bkHinge(v) { return bookEase(SO_HINGE, 1, v); }
/* how far south the book goes to clear the paper, in css px */
function bkSlideDy(S, b) {
  var L = S.layout, f = S.fld;
  if (!L || !f || !f.sheet) return 0;
  var mm = f.sheet.w * L.k / 840, south = L.oy + f.sheet.h * L.k;
  return Math.max(0, south + SO_CLEAR_MM * mm - b.y);
}
/* ==== 2026-09-20: THE SHADOW LANDS WITH THE LEAVES. Tommy: "the field
   guide + journal have a bit of a delay after being fully opened up
   before the shadow renders, is that a deliberate delay or what?" It was
   not a design; it was two left-overs of R67, when the readable leaves
   FADED in over a GL spread: (1) `framing` held the book for 220 ms after
   the hinge landed so that fade could finish, and under L nothing fades
   (styles.css sets every motion stage to opacity 1, transition none), so
   the beat was a wait for nothing; (2) the panel's own cast on the desk
   (`.book-body`'s sun box-shadow) belongs to the `reading` stage alone —
   every motion stage paints its halves itself, and of the two halves only
   the hinged leaf carried a cast (`.book-hinge::after`); the block's half
   had none on the paper (its GL shadow is thrown on the OAK, which the
   sheet covers where an open book lies). So the block's cast appeared as
   a step when `reading` landed, 220 ms after the leaves did. Now the
   block's half throws its cast from the first frame of the carry
   (styles.css `.book-inner::after`, on `--carry-on`, written below), the
   leaf's comes in with the hinge as before, and `framing` is one tick
   long, as `retracting` is — kept so every path sees the same sequence.
   The reopen-while-retracting path used the same two numbers; both are
   nought for the same reason. */
var BOOK_FRAME_MS = 0;       /* was 220: R67's leaves-fade, gone under L */
var BOOK_READER_DELAY_MS = 0; /* was 420: styles.css's reader delay, gone with it */
var LIFT = [{ v: 0, from: 0, to: 0, t0: 0, ms: 1 }, { v: 0, from: 0, to: 0, t0: 0, ms: 1 },
            { v: 0, from: 0, to: 0, t0: 0, ms: 1 }, { v: 0, from: 0, to: 0, t0: 0, ms: 1 }];
var liftRaf = 0, liftNodes = null, liftKey = ['', ''];
var bookFrameTimer = [0, 0];

function bookStage(i, stage) {
  var n = document.getElementById(i ? 'journal' : 'key');
  if (n && n.getAttribute('data-book-motion') !== stage) {
    n.setAttribute('data-book-motion', stage);
    if (i) leafCloneSync();
  }
}
function bookStageOf(i) {
  var n = document.getElementById(i ? 'journal' : 'key');
  return n ? n.getAttribute('data-book-motion') : null;
}
/* ==== BK1: WHAT AN OPEN BOOK IS MADE OF, AND WHERE ===================
   R67 had two answers: the readable DOM panel in the map view, and a
   physical spread at a rear station everywhere else. The card asks for
   one place — and the place Tommy names is the panel's, which is ON the
   map. That is the fact that decides which of the two representations can
   be the book there:

   THE MAP'S PRINT IS DRAWN AFTER THE DESK, WITH THE DEPTH BUFFER THROWN
   AWAY. This piece draws between the oak and the sheet (`O.piece`), and
   objects.js's `depthBack` clears depth and turns the test off the moment
   the sheet is down; the ground, the marks, the voyage, the lettering and
   the card (engine.js's steps 3 to 7) then paint over everything inside
   the paper's window. So a physical spread laid on the map is INVISIBLE
   by construction — measured on this branch: the GL spread drawn at the
   panel's rectangle with the reader standing showed nothing but its gutter through the
   print. R67's rear station was north of the sheet, outside that window,
   which is why it could be seen at all.

   The one thing that can lie on the map is DOM, and R30's plate is how
   DOM is laid on this desk. So the panel IS the book, in every view, and
   it takes the same projective plate the two labels take — the reader
   sees the same open journal, in the same place, from the fit, from the
   desk pose and from across the room. The physical mesh keeps the one
   thing DOM cannot do: the covers TURNING. While a book is opening,
   closing or being carried, the spread is the object; the instant it has
   landed (`reading`), the panel is. */
var panelLaid = [true, true];
function readerSubstitutes(i) {
  return bookStageOf(i) === 'reading' && panelLaid[i];
}
function bookFrame(i, ms) {
  if (bookFrameTimer[i]) clearTimeout(bookFrameTimer[i]);
  bookStage(i, 'framing');
  bookFrameTimer[i] = setTimeout(function () {
    bookFrameTimer[i] = 0;
    var n = document.getElementById(i ? 'journal' : 'key');
    if (n && n.getAttribute('data-open') === 'true' && LIFT[2 + i].v === 1)
      bookStage(i, 'reading');
    if (O.S && O.S.invalidate) O.S.invalidate();
  }, ms === undefined ? BOOK_FRAME_MS : ms);
}

/* No movement at all under prefers-reduced-motion — not a faster one. And
   none on a phone either: at 820 px and under the two covers are a strip
   along the foot of the plate with no desk behind them (styles.css :866),
   so there is nothing to slide out from under. */
/* R67: AND AT 820 PX AND UNDER THEY ARE NOT ON A DESK AT ALL. styles.css
   (:913) lays the two books as full-width bars along the foot of the
   screen there, with no oak behind them — and O.place was still dragging
   those bars to the GL covers' places with an inline left/top, which is
   R63's OPEN 1: measured at 780 x 1410 on poc/room, `.book` came out 0 px
   wide, `.book-cover` 1 px, and the label landed at x = -51, half off the
   screen. The bars keep their own layout now, which also keeps the new
   sizes out of a layout they are not written for. */
/* PO1 (2026-10-02): A PHONE'S BOOKS ARE NEVER ON THE DESK. Upright the
   Journal is the page and on its side the books are spines at the foot of
   the glass (notes/phone-design.md); both are laid by styles.css in
   phone.js's frame, so they keep their own layout at any width, as the
   bars do, and nothing here moves or lifts them. */
var PHONE = /(^|\s)phone(\s|$)/.test(document.documentElement.className);
/* TB2: a phone path on a tablet's glass (base.njk) */
var TABLET = PHONE && /(^|\s)tablet(\s|$)/.test(document.documentElement.className);
function mobileBars() {
  if (PHONE) return true;
  return !!(window.matchMedia && window.matchMedia('(max-width: 820px)').matches);
}
function liftStill() {
  if (PHONE || !window.matchMedia) return !!PHONE;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
      || window.matchMedia('(max-width: 820px)').matches;
}
function liftStep(now) {
  liftRaf = 0;
  var more = false;
  for (var i = 0; i < LIFT.length; i++) {
    var L = LIFT[i];
    if (L.v === L.to) continue;
    var e = Math.min(1, (now - L.t0) / L.ms);
    /* out on an ease-out cubic (it leaves at once and arrives gently, the
       way a hand lifts a corner), back on an ease-in-out */
    var k = i >= 2 ? e * e * (3 - 2 * e)
                   : (L.to > L.from ? 1 - Math.pow(1 - e, 3) : e * e * (3 - 2 * e));
    L.v = e < 1 ? L.from + (L.to - L.from) * k : L.to;
    if (e >= 1 && i >= 2) {
      if (L.to === 1) bookFrame(i - 2);
      else bookStage(i - 2, 'closed');
    }
    if (e < 1) more = true;
  }
  liftLabels();
  if (O.S && O.S.invalidate) O.S.invalidate();
  if (more) liftRaf = requestAnimationFrame(liftStep);
}
/* ==== BK1: THE TWO GATES =============================================
   Tommy: "when the map is folded away, the field guide and journal should
   always be enclosed, and you shouldn't be able to click on them outside
   of the map view."

   THE READING OF THE NUANCE, written down because it is a choice and not
   a transcription. He asks first for the same open place in the room and
   in the map, then for closed books when FOLDED and no activation OUTSIDE
   the map view. He does not say that every look round the room must slam
   an open book shut — and "leaving an open book is cool" (R67 c) is his
   too. So the rule built here is: the same physical pose is SEEN from an
   unfurled room; FOLDING closes the book; OPENING requires the map view.

   (a) hangs on the fold's OWN state, not on a poll. engine.js's
   `markFold` puts `room-folded` on the root element the instant the first
   crease takes (foldT > 0.0005) and takes it off when the sheet is flat
   again, whichever hand is driving — the corner's own gesture through
   pin.js, `Room.foldAt`, `Room.foldTo` or `Room.unfold` —
   so one observer on that one class cannot miss a path. What it calls is
   the book's own close, the one Esc calls, so the leaves retract, the
   covers hinge shut and the book slots back under the paper exactly as
   R29 choreographed it.
   (b) is the map-view test, applied to each way in separately: the cover
   stops taking the pointer (gateCovers), the hover lift refuses (here),
   and books.js refuses the keyboard and every programmatic open. */
function mapView() {
  var B = window.RoomBooks;
  return !B || !B.mapView || B.mapView();
}
var foldWatched = false;
function watchFold() {
  if (foldWatched || !window.MutationObserver) return;
  foldWatched = true;
  var r = document.documentElement, was = r.classList.contains('room-folded');
  new MutationObserver(function () {
    var on = r.classList.contains('room-folded');
    if (on === was) return;
    was = on;
    /* not while a deep link is still on its way to the map view: it is
       about to unfold this sheet itself (books.js, `land`) */
    var B = window.RoomBooks;
    if (on && B && B.closeOne && !(B.landing && B.landing())) B.closeOne();
  }).observe(r, { attributes: true, attributeFilter: ['class'] });
}
function liftSet(i, on) {
  /* BK1 (b): a book is not picked up outside the map view. Only the two
     hover channels are gated — the hinge channels [2],[3] must always be
     able to run a book SHUT, which is what the fold does. */
  if (on && i < 2 && !mapView()) on = false;
  var L = LIFT[i], to = on ? 1 : 0;
  var still = liftStill();
  if (L.to === to && (!still || L.v === to)) return;
  if (still) {
    /* Reduced motion and the <=820px tab layout have no moving desk object.
       Set every channel, including the hinge channels, synchronously. This
       also leaves no rAF capable of completing an obsolete reversal later. */
    L.v = L.from = L.to = to;
    if (i >= 2) {
      var bi = i - 2;
      if (bookFrameTimer[bi]) { clearTimeout(bookFrameTimer[bi]); bookFrameTimer[bi] = 0; }
      bookStage(bi, on ? 'reading' : 'closed');
    }
    liftLabels();
    if (O.S && O.S.invalidate) O.S.invalidate();
    return;
  }
  L.from = L.v; L.to = to; L.t0 = (window.performance || Date).now();
  L.ms = i >= 2 ? BOOK_HINGE_MS : (on ? LIFT_OUT_MS : LIFT_BACK_MS);
  if (!liftRaf) liftRaf = requestAnimationFrame(liftStep);
}
/* the label is the one part of a book that is still type, so it is still
   DOM: it is given the book's own offset and nothing else */
/* ==== L: THE LEAF IS DOM, AND IT TURNS ON THIS CLOCK =================
   Tommy: "either the writing needs to be in the opened book or we do
   something different?" — the writing is in it. styles.css lays the far
   page in a hinge (`.book-hinge`) whose back is the cover; what is written
   here, on every frame of liftStep, is the hinge's angle off the same
   eased channel that carries the block (bookPose's `hinge`), as a custom
   property on the aside and never as a css transition — the carry and the
   turn keep the one clock they have. Signed: the key's leaf is the west
   page and turns about its east edge, the journal's the mirror.
   --leaf-on is the hand-over from the block's GL cloth to the panel's own
   over the first LEAF_ON of the hinge (styles.css says why). */
var LEAF_ON = 0.08;
var hingeKey = ['', ''];
function liftHinge() {
  var ids = ['key', 'journal'];
  for (var i = 0; i < 2; i++) {
    var hinge = bkHinge(LIFT[2 + i].v);
    var ang = (i ? -1 : 1) * 180 * (1 - hinge), on = Math.min(1, hinge / LEAF_ON);
    var hk = ang.toFixed(2) + '|' + on.toFixed(3) + '|' + LIFT[2 + i].v.toFixed(3);
    if (hingeKey[i] === hk) continue;
    hingeKey[i] = hk;
    var bk = document.getElementById(ids[i]); if (!bk) continue;
    bk.style.setProperty('--hinge', ang.toFixed(2) + 'deg');
    bk.style.setProperty('--hinge-on', hinge.toFixed(3));
    bk.style.setProperty('--leaf-on', on.toFixed(3));
    /* the block's cast on the paper comes in with the carry (bookPose's
       own ease) and goes out with it on the way home */
    bk.style.setProperty('--carry-on', bkCarry(LIFT[2 + i].v).toFixed(3));
  }
  leafCloneSync();
}
/* THE JOURNAL'S FAR PAGE IS A COLUMN, NOT AN ELEMENT: its writing is the
   east column of one two-column flow (`.journal-flow`, paged by books.js
   with a transform), which no rigid leaf can carry. So while the leaf is
   in the air its front holds a CLONE of that flow at the same width and
   the same offset — the same column breaks, so the same words on the
   page — and styles.css cuts the real flow to its west column meanwhile.
   Landed or shut, the clone goes. Re-made only when the flow has changed
   under it (a page turned, the essay written in after the open). */
var leafClone = null, leafCloneN = -1, leafCloneT = '';
function leafCloneSync() {
  var bk = document.getElementById('journal'), stage = bookStageOf(1);
  var host = bk && bk.querySelector('.book-hinge-front');
  var flow = document.getElementById('journal-flow');
  var want = host && flow && stage && stage !== 'closed' && stage !== 'reading' && !liftStill();
  if (!want) {
    if (leafClone) { leafClone.remove(); leafClone = null; }
    return;
  }
  var n = flow.childElementCount, t = flow.style.cssText;
  if (leafClone && leafCloneN === n && leafCloneT === t) return;
  if (leafClone) leafClone.remove();
  var clip = document.createElement('div');
  clip.className = 'book-hinge-clip';
  clip.setAttribute('aria-hidden', 'true');
  var c = flow.cloneNode(true);
  c.removeAttribute('id');
  var dup = c.querySelectorAll('[id]');
  for (var j = 0; j < dup.length; j++) dup[j].removeAttribute('id');
  clip.appendChild(c);
  host.appendChild(clip);
  leafClone = clip; leafCloneN = n; leafCloneT = t;
}
function liftLabels() {
  if (!liftNodes) return;
  liftHinge();
  for (var i = 0; i < 2; i++) {
    var n = liftNodes[i]; if (!n) continue;
    var v = LIFT[i].v, dx = (i ? 1 : -1) * BOOK_OUT * v, dy = BOOK_OUT * v;
    var key = v ? dx.toFixed(2) + ',' + dy.toFixed(2) : '';
    if (liftKey[i] === key) continue;
    liftKey[i] = key;
    n.style.transform = v ? 'translate(' + dx.toFixed(2) + 'px,' + dy.toFixed(2) + 'px)' : '';
  }
}
/* R29: WHERE THE HAND ACTUALLY IS, not where the last enter/leave pair
   said it was. While a panel is open its cover carries pointer-events:none,
   so a pointer that wanders off the book is never reported leaving it: the
   `over` flag goes stale open and stays stale. One pointermove listener
   keeps the last position and the beat ends with a hit test. */
var liftPt = [-1, -1], liftBtns = [null, null], liftHeld = [false, false], liftHits = [null, null];
/* IS THE HAND ON THE COVER. Two askers: the hover (`hand` unset), where a
   focused cover counts as held — a reader who tabbed to it sees it lift;
   and the PUT-DOWN (`hand` set: home() and liftPointer), where only the
   POINTER is the hand. Tommy, 2026-09-14: "field guide closes to hover
   state but doesn't then auto slide away … if i click esc or onto the
   map" — the click that opened the book had FOCUSED the cover, Esc left
   it focused, and a click on the map does not move focus (the canvas's
   pointerdown prevents the default), so the book was held for ever.
   Focus is the hand only when no pointer has been seen at all. */
function liftOnBtn(btn, hand, hit) {
  if (!btn) return false;
  var focused = document.activeElement === btn;
  if (liftPt[0] < 0) return focused;
  if (!hand && focused) return true;
  var n = document.elementFromPoint(liftPt[0], liftPt[1]);
  /* the hit box (hitsAtPose) is the book too */
  return !!(n && (n === btn || btn.contains(n) || (hit && (n === hit || hit.contains(n)))));
}
function liftPointer(e) {
  liftPt[0] = e.clientX; liftPt[1] = e.clientY;
  /* a book held lifted after its panel shut is let go the moment the hand
     is somewhere else — belt and braces for the enter/leave pair that the
     pointer-events switch may never deliver */
  for (var i = 0; i < 2; i++)
    if (liftHeld[i] && !liftOnBtn(liftBtns[i], true, liftHits[i])) { liftHeld[i] = false; liftSet(i, false); }
}
function wireLift() {
  if (O._liftWired || NO_BOOKS) return;
  watchFold();                      /* BK1 (a): folding the map shuts them */
  var ids = ['key', 'journal'], ok = false;
  liftNodes = [];
  for (var i = 0; i < 2; i++) {
    var bk = document.getElementById(ids[i]);
    var btn = document.getElementById(ids[i] + '-tab');
    liftNodes.push(bk ? bk.querySelector('.book-face') : null);
    if (!bk || !btn) continue;
    ok = true;
    liftBtns[i] = btn;
    (function (bk, btn, i) {
      /* Tommy 2026-09-14: "the book in front is a replacement for the
         closed book behind" — so while a book is OPEN the shut one is
         GONE from the desk (see LIFT), and it comes back when the panel
         is shut by whichever path shuts it (the button, Esc, history),
         which is why the open state is watched on the element and not
         inferred from the click. The click itself is books.js's and
         untouched. */
      var over = false, was = null, beat = 0, stw = false;
      /* the end of the beat: the leaves are gone and the book has been
         stood on the desk in the hand's pose. Unless the hand is still on
         it, it slots home — LIFT_BACK_MS, the hover's own return. */
      function home() {
        beat = 0;
        over = liftOnBtn(btn, true, liftHits[i]);
        liftHeld[i] = over;
        if (!over) liftSet(i, false);
      }
      function sync() {
        /* ST2 (2026-10-03): A BOOK PUT AWAY BY A TURN IS SHUT ON THE DESK.
           On the phone a turn on to the side stows the open book (phone.js
           `.stowed`): still open at its page, not drawn. Its desk object
           was left at its reading station, open, and lay under the sheet
           there (Tommy's third recording: "The field guide sits under the
           sheet in a weird way when you go to landscape"). Stowed counts as
           shut here, and stowing or bringing it out is AT ONCE, with no
           carry: the turn is the motion. */
        var st = bk.classList.contains('stowed');
        var open = bk.getAttribute('data-open') === 'true' && !st;
        var inst = st !== stw;
        stw = st;
        if (open === was) {                      /* a hover change, not an open/shut */
          if (!open && !beat) liftSet(i, over);
          return;
        }
        was = open;
        if (beat) { clearTimeout(beat); beat = 0; }
        if (bookFrameTimer[i]) { clearTimeout(bookFrameTimer[i]); bookFrameTimer[i] = 0; }
        liftHeld[i] = false;
        if (inst) {
          var Gs = LIFT[2 + i];
          Gs.v = Gs.from = Gs.to = open ? 1 : 0;
          bookStage(i, open ? 'reading' : 'closed');
          if (!open) liftSet(i, false);
          liftLabels();
          if (open && O.S && O.S.VP) panelsAtPose(O.S);
          if (O.S && O.S.invalidate) O.S.invalidate();
          return;
        }
        if (open) {
          /* The physical object goes first: carry the folded book to its
             reading station and hinge the moving leaf. CSS does not frame
             the readable DOM leaves until this movement is nearly home. */
          bookStage(i, 'opening');
          /* L: and the plate on the same tick, so no paint can find the
             panel at its css home before the first frame lays it */
          if (O.S && O.S.VP) panelsAtPose(O.S);
          /* SO2 (2026-10-01): THE HAND DOES NOT PUT IT DOWN FIRST. The hover
             lift (10 px out, two millimetres up) used to ease back on the
             click, so the book went back towards the paper for a beat and
             then slid away from it: the label rose and then fell (Tommy:
             "a slight pop when you open the journal or field guide, like
             the label moves"). It is held through the slide and let go as
             the carry lifts the book, where the arc hides it. */
          if (liftStill()) liftSet(i, false);
          else setTimeout(function () { liftSet(i, false); }, BOOK_HINGE_MS * SO_CARRY[0]);
          /* Reopening during the 220 ms reader-retract window finds the
             physical book already fully open. There will be no hinge rAF to
             call bookFrame, so explicitly re-arm the reader/substitution
             beat; otherwise data-book-motion would remain `opening`. */
          var G = LIFT[2 + i];
          if (G.v === 1 && G.to === 1) {
            if (liftStill()) bookStage(i, 'reading');
            else bookFrame(i, BOOK_READER_DELAY_MS + BOOK_FRAME_MS);
          } else liftSet(2 + i, true);
          return;
        }
        /* Closing is exactly the reverse sequence: retract the readable
           frame, hinge the still-open physical leaves shut, then return the
           folded cover to its storage anchor. No closed mesh is substituted
           beneath a panel that is still fading. */
        bookStage(i, 'retracting');
        if (O.S && O.S.invalidate) O.S.invalidate();
        beat = setTimeout(function () {
          beat = 0;
          bookStage(i, 'closing');
          liftSet(2 + i, false);
          beat = setTimeout(home, BOOK_HINGE_MS + LIFT_SETTLE);
        }, PANEL_SHUT_MS);
      }
      btn.addEventListener('pointerenter', function () { over = true; sync(); });
      btn.addEventListener('pointerleave', function () { over = false; liftHeld[i] = false; sync(); });
      btn.addEventListener('focus', function () { over = true; sync(); });
      btn.addEventListener('blur', function () { over = false; liftHeld[i] = false; sync(); });
      btn.addEventListener('click', function () { setTimeout(sync, 0); });
      /* THE HIT BOX HANDS THE COVER THE HAND (hitsAtPose): it lies over
         the cover, so enter and leave are its and go to the same `over`;
         a click is the button's own click, with focus on the button as a
         click on the button would leave it. Its mousedown keeps the
         default off so the focus does not fall to the body between the
         press and the click (a blur would drop the book for a frame). */
      var hit = HIT_ON ? bookHitEl(bk) : null;
      if (hit) {
        liftHits[i] = hit;
        hit.addEventListener('pointerenter', function () { over = true; sync(); });
        hit.addEventListener('pointerleave', function () { over = false; liftHeld[i] = false; sync(); });
        hit.addEventListener('mousedown', function (e) { e.preventDefault(); });
        hit.addEventListener('click', function () {
          if (btn.focus) btn.focus({ preventScroll: true });
          btn.click();
        });
      }
      if (window.MutationObserver)
        new MutationObserver(sync).observe(bk, { attributes: true, attributeFilter: ['data-open', 'class'] });
      /* AND SEEDED ONCE AT THE START, without the choreography. `?journal=1`
         and `?book=1` open a panel before this is wired and nothing used to
         tell the desk, so the shut book sat there under its own open one.
         A book merely CLOSED at load has not just been put down, though, so
         this is a seed and not a sync: `was` is set, and nothing moves. */
      was = bk.getAttribute('data-open') === 'true';
      if (was) {
        var G0 = LIFT[2 + i]; G0.v = G0.to = G0.from = 1;
        bookStage(i, 'reading');
        if (O.S && O.S.invalidate) O.S.invalidate();
      } else bookStage(i, 'closed');
    })(bk, btn, i);
  }
  if (ok) {
    O._liftWired = true;
    liftLabels();                     /* L: the hinge's angle before its first frame */
    document.addEventListener('pointermove', liftPointer, { passive: true });
  }
}
O.lift = function (i) { return LIFT[i] ? LIFT[i].v : 0; };
/* WHERE THE BOOKS LIE. survey-map.js :7131 works it out from where the
   sheet lands, and so does this: a hand's breadth of cloth past the
   paper's southern edge, the spine out past its western (and the
   journal's out past its eastern), held inside the window. */
/* BV1 A2: 0 while the books lie on the open map's foot, 1 at the desk's
   places (the fold, the room, the laptop, the pinch out into the paper) */
/* BV2 (2026-10-06): AND THEY ARE TAKEN OFF THE PAPER BEFORE IT FOLDS. Tommy,
   of the fold: "the labels on the books immediately disappear and ... the
   sheet just goes through the books". So the fold no longer carries them
   along its own travel: the moment a hand takes the corner (or the sheet is
   anything but flat) they slide off to the desk over A2_SLIDE ms, on their
   own clock, ahead of the flap; and slide back on when it lies flat again. */
var A2_SLIDE = 240, a2T = { v: 0, from: 0, to: 0, t0: 0 };
function a2Out(S) {
  var c = S.cam || {}, fo = O.foldOn ? (O.foldT || 0) : 0;
  var st = Math.min(1, Math.max(0, ((Math.max(c.room || 0, c.lap || 0)) - 0.02) / 0.28));
  var want = fo > 0.001 || (O.foldGrab || 0) > 0 ? 1 : 0, now = performance.now();
  if (O.flatCapture) want = a2T.to;   /* the fold's print sets the sheet flat for a moment (engine.js printSnap) */
  if (want !== a2T.to) { a2T.from = a2T.v; a2T.to = want; a2T.t0 = now; }
  var u = Math.min(1, (now - a2T.t0) / A2_SLIDE);
  a2T.v = a2T.from + (a2T.to - a2T.from) * u * u * (3 - 2 * u);
  if (u < 1 && O.S && O.S.invalidate) O.S.invalidate();   /* the slide's own frames */
  return Math.max(a2T.v, st, (S.layout && S.layout.wide) || 0);
}
O.a2Out = function (S) { return BK_A2 && PHONE && S.layout && S.layout.side ? a2Out(S) : 1; };
O.books = function (S, raw) {
  var L = S.layout, f = S.fld;
  var south = L.oy + f.sheet.h * L.k;
  /* R63: each book is placed by its OWN size. The same two rules as
     before — the same fraction of the cover shows past the paper's
     southern edge, and the spine hangs the same fraction of its own width
     past the sheet's side — so with the default dims every number below
     is the one E4 and R22 struck, to the pixel. The bottom is still held
     inside the window, which is what a book with a real 190 mm of depth
     runs into: it cannot show 62 % of itself AND keep its head on the
     glass, so it is the SHOW that gives and the book goes further under
     the paper. Measured at the fit in REPORT-room-R63.md. */
  var d = bookDims(S), dA = d[0], dB = d[1];
  var shA = BOOK_SHOW * dA.h / BK_H, shB = BOOK_SHOW * dB.h / BK_H;
  var ovA = 22 * dA.sp / BK_SPINE, ovB = 22 * dB.sp / BK_SPINE;
  var gy1 = L.ref ? L.ref.y1 : L.ch, gy0 = L.ref ? L.ref.y0 : 0;   /* MM1: the reference window */
  var topA = Math.round(Math.max(gy0 + 10, Math.min(gy1 - dA.h - 8, south - dA.h + shA)));
  var topB = Math.round(Math.max(gy0 + 10, Math.min(gy1 - dB.h - 8, south - dB.h + shB)));
  /* FB2 (2026-10-04): upright in the room the glass they are held inside
     is the SIDEWAYS one (RU3), as it is for the sheet and the card. Held
     inside the narrow upright glass, the Field Book was pushed out from
     under the packet while the paper's show still darkened it where the
     packet would lie (Tommy: "the shadow on the field book is wrong"), and
     the Journal lay under the card. */
  var gx0 = L.ref ? L.ref.x0 : L.ah ? (L.cw - L.aw) / 2 : 0, gx1 = L.ref ? L.ref.x1 : L.ah ? gx0 + L.aw : L.cw;
  var a = { x: Math.round(Math.max(gx0 + 6, L.ox - ovA)), y: topA, w: dA.w, h: dA.h, z: dA.z, sp: dA.sp };
  var east = L.ox + f.sheet.w * L.k;
  var b = { x: Math.round(gx1 - Math.max(6, gx1 - east - ovB) - dB.w), y: topB, w: dB.w, h: dB.h, z: dB.z, sp: dB.sp };
  /* PH10: sideways the phone is the map, and the books are laid off the
     glass altogether (their spines are DOM, styles.css PO1) */
  if (L.side) {
    /* DE1 (2026-10-03): only while the sheet lies open. Off the glass was
       444 mm south of the sheet's centre for the journal, and DD1 brought
       the desk's front edge out to it: folded or stood up, the books hung
       off a desk that ended short of them and then reached "unrealistically
       south" (Tommy, on his iPhone). As the sheet folds, or the reader
       stands, they come to the desk's own places, the desktop's to the mm:
       the paper's show with each book's foot on the 264 mm front edge. */
    var c = S.cam || {}, fo = O.foldOn ? (O.foldT || 0) : 0;
    var st = Math.min(1, Math.max(0, ((Math.max(c.room || 0, c.lap || 0)) - 0.02) / 0.28));
    /* US2 (2026-10-04): seated at the real desk (engine.js upSeat) they lie
       where they lie on it, fold or no fold: DD1 holds the real desk's front
       edge under them, so books leaving with the unfold dragged the desk's
       edge after them (Tommy: "the desk 'grows' as the map unfolds") */
    var on = (c.seat || 0) > 0 ? 1 : Math.max(fo, st);
    var mmK = f.sheet.w * L.k / 840, cyS = L.oy + f.sheet.h * L.k / 2;
    var front = cyS + (O.DESK_MM ? O.DESK_MM.s - 1 : 263) * mmK;
    var off = (L.gl ? L.gl.y1 : L.ah ? L.ah + (L.ch - L.ah) / 2 : L.ch) + 60 + dB.z;   /* RU3; MP1 the glass in the world */
    var hA = Math.min(south - dA.h + shA, front - dA.h), hB = Math.min(south - dB.h + shB, front - dB.h);
    a.y = Math.round(off + (hA - off) * on);
    b.y = Math.round(off + (hB - off) * on);
    /* BV1 A2: open on the glass they lie on its foot, one in each corner;
       folding, standing or the pinch into the paper takes them to the
       desk's own places (`a2Out`) */
    if (BK_A2) {
      var g = L.gl || { x0: 0, y0: 0, x1: L.cw, y1: L.ch }, m = a2Out(S);
      var ax1 = a.x, bx1 = b.x, ay1 = Math.round(hA), by1 = Math.round(hB);
      var ax0 = g.x0 + A2_IN, bx0 = g.x1 - A2_IN - dB.w;
      var ay0 = g.y1 - A2_PEEK, by0 = g.y1 - A2_PEEK;
      a.x = Math.round(ax0 + (ax1 - ax0) * m); a.y = Math.round(ay0 + (ay1 - ay0) * m);
      b.x = Math.round(bx0 + (bx1 - bx0) * m); b.y = Math.round(by0 + (by1 - by0) * m);
    }
  }
  /* R22: and where the hand has picked one up. AFTER the rounding, so the
     travel is smooth instead of stepping a pixel at a time; `raw` is the
     DOM's copy, which is laid out at the resting place and carries the
     same offset as a transform (liftLabels) instead. */
  if (!raw) {
    a.x -= BOOK_OUT * LIFT[0].v; a.y += BOOK_OUT * LIFT[0].v;
    b.x += BOOK_OUT * LIFT[1].v; b.y += BOOK_OUT * LIFT[1].v;
  }
  return [a, b];
};
/* THE READING POSE. The canonical hinged mesh is two cover-widths wide with
   its spine in the middle. Folded, its fixed half exactly occupies the
   stored cover: west of the spine for the journal and east for the field
   book. The hand opens the book INTO the panel's rectangle — the same
   rectangle the map view's reader has always stood in — and running v
   backwards closes the cover and puts it back in the shut book's own
   place. Two things move on the same ease and nothing else does: the
   rectangle's north edge and its far side. The south edge is the shut
   cover's south edge from the first frame to the last. */
/* BK1: THE PANEL'S OWN BOX, read from the element that draws it, once per
   window. `.book-body` / `.journal-body` carry their width in css —
   min(44rem, 100vw - 3.2rem, (70dvh - 20px)*4/3) and `--jw` — and that
   width does NOT move while the panel opens (only its max-height does),
   so a shut book can be asked where its open self will stand. The HEIGHT
   is the one thing that animates, so it is measured only while the panel
   is settled and never read off a collapsed one. Both are layout reads,
   so both are keyed on the window and neither happens in a frame that has
   not resized. */
var PANEL = [null, null];
function panelEl(i) {
  var n = document.getElementById(i ? 'journal' : 'key');
  return n && n.querySelector(i ? '.journal-body' : '.book-body');
}
function panelBox(S, i, settled) {
  var L = S.layout, k = L.cw + 'x' + L.ch + '@' + (S.dpr || 1);
  var c = PANEL[i];
  if (c && c.k === k && (c.h > 0 || !settled)) return c;
  var el = panelEl(i);
  if (!el) return c && c.k === k ? c : null;
  var w = el.offsetWidth;
  var h = settled ? el.offsetHeight : (c && c.k === k ? c.h : 0);
  c = PANEL[i] = { k: k, w: w, h: h };
  return c;
}
function panelWidth(S, i) {
  var c = panelBox(S, i, false);
  return c ? c.w : 0;
}
/* the open spread's rectangle on the desk, in css px: the panel's width,
   the book's own proportion, and the shut cover's own corner */
/* JB1 (2026-10-06): AND IT IS ON THE SCREEN. The panel hangs from the shut
   cover's corner, and in a smaller window (a 13" MacBook's Safari, 1440 x
   ~790) the journal's corner is at the window's east edge, so the spread
   opened mostly off it. Tommy: "my partner is reporting the journal on her
   macbook air in safari, the journal launches mostly off screen". The
   corner stays the anchor; when the box it hangs would cross an edge it is
   moved in by exactly that much, plus a margin. One number pair, read by
   the drawn block (bookTarget's home), the plate's origin and the css
   (the panel's margins), so all three move together. */
var PANEL_EDGE = 16;
function panelOff(S, i, box) {
  var L = S.layout;
  if (!L || !box || !(box.w > 0) || !(box.h > 0) || mobileBars()) return [0, 0];
  var r = O.books(S, true)[i];
  if (!r) return [0, 0];
  var x = i ? r.x + r.w - box.w : r.x, y = r.y + r.h - box.h, dx = 0, dy = 0;
  if (x + box.w > L.cw - PANEL_EDGE) dx = L.cw - PANEL_EDGE - box.w - x;
  if (x + dx < PANEL_EDGE) dx = PANEL_EDGE - x;
  if (y + box.h > L.ch - PANEL_EDGE) dy = L.ch - PANEL_EDGE - box.h - y;
  if (y + dy < PANEL_EDGE) dy = PANEL_EDGE - y;
  return [dx, dy];
}
function bookSpread(S, i, b, dim) {
  var w = panelWidth(S, i);
  if (!(w > 0)) { w = dim.w * 2; }            /* no panel in the page: the book's own size */
  var h = w * dim.h / (dim.w * 2);            /* the leaves keep their proportion */
  return { w: w, h: h, x: i ? (b.x + b.w - w) : b.x, y: b.y + b.h - h };
}
function bookEase(a, b, v) {
  var t = (v - a) / (b - a);
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  return t * t * (3 - 2 * t);
}
/* ==== L: WHERE THE BLOCK IS CARRIED TO ================================
   The reader's panel in the map view is a rectangle ON THE GLASS — css
   px, the shut cover's own south-west corner, flat (BK1: "when you're in
   the map view, the book goes over the map") — and the block is a solid
   on the DESK, seen through a pitched camera. For the panel to lie on the
   block with no substitution, the block has to be carried to the place on
   the desk that the camera shows AT that rectangle: so the panel's four
   corners are taken back through the camera on to the plane of the
   block's top (a homography, from the shut cover's own rectangle at that
   height, inverted) and the world rectangle that best fits them is the
   carry's target. A world rectangle cannot land exactly on a screen
   rectangle through a perspective; what is left over at each corner
   (`res`) is carried into the panel's plate with the carry, so that at
   carry 1 the plate is the identity and the reader Tommy has is
   byte-for-byte the one he keeps, and at carry 0 it is the block's own
   top face. The fit is struck in the map view and HELD when the reader
   stands (k > 0): a book does not slide across the desk because the
   reader got up. Without a panel (no element, the phone's bars, no
   camera yet) bookSpread's rectangle is the target, as before. */
function sqToQuad(q) {
  var x0 = q[0][0], y0 = q[0][1], x1 = q[1][0], y1 = q[1][1],
      x2 = q[2][0], y2 = q[2][1], x3 = q[3][0], y3 = q[3][1];
  var sx = x0 - x1 + x2 - x3, sy = y0 - y1 + y2 - y3;
  var dx1 = x1 - x2, dx2 = x3 - x2, dy1 = y1 - y2, dy2 = y3 - y2, den = dx1 * dy2 - dx2 * dy1;
  if (Math.abs(den) < 1e-12) return null;
  var g = (sx * dy2 - dx2 * sy) / den, h = (dx1 * sy - sx * dy1) / den;
  return [x1 - x0 + g * x1, x3 - x0 + h * x3, x0, y1 - y0 + g * y1, y3 - y0 + h * y3, y0, g, h, 1];
}
function inv3(m) {
  var a = m[0], b = m[1], c = m[2], d = m[3], e = m[4], f = m[5], g = m[6], h = m[7], k = m[8];
  var A = e * k - f * h, B = -(d * k - f * g), C = d * h - e * g;
  var det = a * A + b * B + c * C;
  if (!(Math.abs(det) > 1e-12)) return null;
  return [A / det, -(b * k - c * h) / det, (b * f - c * e) / det,
          B / det, (a * k - c * g) / det, -(a * f - c * d) / det,
          C / det, -(a * h - b * g) / det, (a * e - b * d) / det];
}
function apply3(m, x, y) {
  var w = m[6] * x + m[7] * y + m[8];
  return [(m[0] * x + m[1] * y + m[2]) / w, (m[3] * x + m[4] * y + m[5]) / w];
}
var TGT = [null, null];
function bookTarget(S, i, b, dim) {
  var sp = bookSpread(S, i, b, dim), box = PANEL[i];
  if (!box || !(box.w > 0) || !(box.h > 0) || mobileBars() || !S.VP || !S.camEye) return sp;
  var k = Math.max(S.cam.room || 0, S.cam.lap || 0);
  if (k > 0.0005 && TGT[i]) return TGT[i];
  var L = S.layout, f = S.fld, mm = f.sheet.w * L.k / 840;
  var off = panelOff(S, i, box);                                    /* JB1 */
  var home = { x: (i ? (b.x + b.w - box.w) : b.x) + off[0], y: b.y + b.h - box.h + off[1], w: box.w, h: box.h };
  /* JB2 (2026-10-07): AND WHAT IS DRAWN IS ON THE SCREEN. The panel is laid
     in perspective (LEAF_PERSP), the fitted rectangle projected, not the css
     box: its near edge is wider than the box JB1 moves in, and it stood 20
     to 37 px past the window's edge at every size (desktop-suite D4, his own
     2194 x 1234 too). So the drawn quad itself is held inside the margin:
     when its corners cross an edge the target is nudged in by the overflow
     and fitted again, which settles in one pass. */
  var nx = 0, ny = 0, ref = [[b.x, b.y], [b.x + b.w, b.y], [b.x + b.w, b.y + b.h], [b.x, b.y + b.h]];
  var w1, x1, y1, h1, z1, pts, top, res, hc;
  for (var nudge = 0; nudge < 3; nudge++) {
  hc = [[home.x + nx, home.y + ny], [home.x + nx + home.w, home.y + ny], [home.x + nx + home.w, home.y + ny + home.h], [home.x + nx, home.y + ny + home.h]];
  w1 = box.w; x1 = 0; y1 = 0; h1 = 0; z1 = 0; pts = null;
  /* the height of the top depends on the scale, which depends on the width
     the fit gives: two passes settle it to well under a pixel */
  for (var pass = 0; pass < 2; pass++) {
    z1 = BOOK_ON_PAPER_MM * mm + dim.z * (w1 / (dim.w * 2));
    var q = ref.map(function (p) { return Room.scenePoint(p[0], -p[1], z1); });
    if (!q[0] || !q[1] || !q[2] || !q[3]) return sp;
    var M = sqToQuad(q), Mi = M && inv3(M);
    if (!Mi) return sp;
    pts = hc.map(function (p) { var uv = apply3(Mi, p[0], p[1]); return [b.x + uv[0] * b.w, b.y + uv[1] * b.h]; });
    x1 = (pts[0][0] + pts[3][0]) / 2; var xr = (pts[1][0] + pts[2][0]) / 2;
    y1 = (pts[0][1] + pts[1][1]) / 2; var yb = (pts[2][1] + pts[3][1]) / 2;
    w1 = xr - x1; h1 = yb - y1;
  }
  if (!(w1 > 0) || !(h1 > 0)) return sp;
  top = [[x1, y1], [x1 + w1, y1], [x1 + w1, y1 + h1], [x1, y1 + h1]]
    .map(function (p) { return Room.scenePoint(p[0], -p[1], z1); });
  res = (top[0] && top[1] && top[2] && top[3])
    ? hc.map(function (p, c) { return [p[0] - top[c][0], p[1] - top[c][1]]; }) : null;
  if (!LEAF_PERSP || !res || mobileBars()) break;
  var qx = top.map(function (p) { return p[0]; }), qy = top.map(function (p) { return p[1]; });
  var lo = Math.min.apply(null, qx), hi = Math.max.apply(null, qx), tp = Math.min.apply(null, qy), bt = Math.max.apply(null, qy);
  var dx = hi > L.cw - PANEL_EDGE ? L.cw - PANEL_EDGE - hi : (lo < PANEL_EDGE ? PANEL_EDGE - lo : 0);
  var dy = bt > L.ch - PANEL_EDGE ? L.ch - PANEL_EDGE - bt : (tp < PANEL_EDGE ? PANEL_EDGE - tp : 0);
  if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) break;
  nx += dx; ny += dy;
  }
  var t = { x: x1, y: y1, w: w1, h: h1, res: res, home: home };
  if (!(k > 0.0005)) TGT[i] = t;
  return t;
}
function bookPose(S, i, b, dim) {
  var v = LIFT[2 + i].v, slide = bkSlide(v), carry = bkCarry(v), hinge = bkHinge(v);
  var L = S.layout, f = S.fld, mm = f.sheet.w * L.k / 840;
  /* At the stored pose the two-width canonical box begins one leaf west of
     the field book and at the journal's west edge. Its folded half is thus
     pixel-identical to the shut book before the slide starts. SO1: the
     carry starts from where the slide left it, clear of the paper. */
  var sx = i ? b.x : b.x - b.w, sy = b.y + bkSlideDy(S, b) * slide;
  var sp = bookTarget(S, i, b, dim);
  var w = dim.w * 2 + (sp.w - dim.w * 2) * carry;
  var h = dim.h + (sp.h - dim.h) * carry;
  var x = sx + (sp.x - sx) * carry, y = sy + (sp.y - sy) * carry;
  /* the whole solid is one scale, so a spread struck at reading size has
     boards and a block that thick too and not a squashed book's */
  var scale = w / (dim.w * 2);
  return { i: i, value: v, carry: carry, hinge: hinge,
           x: x, y: y, w: w, h: h, scale: scale,
           /* SO1: up first (the rise is done by a third of the carry, when
              it has gone a fifth of the way) and over in an arc */
           z: (BOOK_ON_PAPER_MM * bookEase(0, 0.34, carry) + SO_ARC_MM * Math.sin(Math.PI * carry)) * mm,
           slide: slide,
           spine: x + w * 0.5, flipped: !!i,
           res: sp.res || null, home: sp.home || null };
}
function bookGroundBox(S, i, b, dim) {
  if (!(LIFT[2 + i].v > 0.0001))
    return { x: b.x, y: b.y, w: b.w, h: b.h, height: dim.z, shown: 1 };
  /* L: the block's own carried footprint — the fixed half, its full
     thickness, and it stays: the panel lies ON it now and never replaces
     it, so the zero box that let the sheet settle where a "gone" book lay
     is gone itself. A SIMPLIFICATION, named: the moving leaf is DOM and
     casts nothing on the oak (its own css cast comes in with the hinge);
     what is thrown is the block's. */
  var p = bookPose(S, i, b, dim), half = p.w * 0.5;
  return { x: i ? p.x : p.x + half, y: p.y, w: half, h: p.h,
           height: p.z + dim.z * p.scale, shown: 1 };
}
/* Read-only evidence hook: where the open panel is standing and whether it
   is the book this frame (BK1). */
O.panelPlate = function () {
  return { laid: panelLaid.slice(), plate: (O._pnlKey || ['', '']).slice(),
           box: PANEL.map(function (c) { return c ? { w: c.w, h: c.h } : null; }) };
};
/* Read-only evidence hook for the visual reviewer and focused source test. */
O.bookPhysical = function (S) {
  S = S || O.S;
  if (!S || !S.layout || !S.fld) return null;
  var b = O.books(S), d = bookDims(S);
  return [bookPose(S, 0, b[0], d[0]), bookPose(S, 1, b[1], d[1])].map(function (p, i) {
    p.ground = bookGroundBox(S, i, b[i], d[i]);
    p.stage = bookStageOf(i);
    p.readerSubstitutes = readerSubstitutes(i);
    return p;
  });
};

/* THE PAPER OWNS THE HAND WHERE IT COVERS A BOOK. The GL depth buffer has
   always drawn this honestly, but the transparent DOM button was still a
   whole upright rectangle: a hand could lift or open either book through
   the sheet hiding its head. Clip the BUTTON, not the painted book, to
   the L-shaped portion outside the paper footprint.

   The cut is made in each cover's own untransformed coordinates, before
   R30's projective cover matrix. Thus the perspective, tilt and bearing
   carry the hit area with the same solid they carry the label. `foldSpan`
   is the footprint of the actual moving/landed paper: full sheet when
   flat, narrowing through both long folds and becoming the south-west
   packet after the cross fold. The lifted/draped flat paper has the same
   cut edge and therefore the same exact footprint. */
var bookClipKey = ['', ''], bookClipState = null, bookGateKey = ['', ''];
function clipClamp(v, a, b) { return v < a ? a : v > b ? b : v; }
/* ==== BK1 (b): AND THE COVER DOES NOT TAKE THE HAND OUTSIDE THE MAP ===
   Tommy: "you shouldn't be able to click on them outside of the map
   view." The hit target is the same element the clip above cuts, so the
   gate is written beside the clip, on the same frame, from the same one
   test — and it is `pointer-events`, which takes nothing off the screen:
   the cover, its label and its shadow are all still drawn, so the room and
   the desk poses are the same picture they were. Written only when it
   changes, like every other DOM write in this file. */
function gateCovers() {
  var live = mapView(), want = live ? '' : 'none', ids = ['key', 'journal'];
  for (var i = 0; i < 2; i++) {
    if (bookGateKey[i] === want) continue;
    var n = document.getElementById(ids[i]);
    var cv = n && n.querySelector('.book-cover');
    if (!cv) continue;
    bookGateKey[i] = want;
    cv.style.pointerEvents = want;
    if (liftHits[i]) liftHits[i].style.pointerEvents = want;
  }
  return live;
}
function clipBooksToPaper(S, books) {
  var ids = ['key', 'journal'];
  gateCovers();
  if (mobileBars()) {
    for (var mi = 0; mi < 2; mi++) {
      var mn = document.getElementById(ids[mi]);
      var mc = mn && mn.querySelector('.book-cover');
      if (mc && bookClipKey[mi]) mc.style.clipPath = '';
      bookClipKey[mi] = '';
    }
    bookClipState = { mobile: true };
    return;
  }
  var L = S.layout, span = { x: 0, y: 0, w: S.fld.sheet.w, h: S.fld.sheet.h };
  if (O.foldOn && O.foldT > 0.0005 && O.foldSpan) span = O.foldSpan();
  var px0 = L.ox + span.x * L.k, px1 = px0 + span.w * L.k;
  var py0 = L.oy + span.y * L.k, py1 = py0 + span.h * L.k;
  var out = [];
  for (var i = 0; i < 2; i++) {
    var n = document.getElementById(ids[i]), cv = n && n.querySelector('.book-cover');
    if (!cv) continue;
    var b = books[i], ox = b.x, oy = b.y - b.z, w = b.w, h = b.h + b.z;
    /* If the current paper footprint does not meet this cover, the whole
       visible book is the hand's. This is normally the journal once the
       first two folds have brought the packet west. */
    var meets = px1 > ox && px0 < ox + w && py1 > oy && py0 < oy + h;
    var cutX, cutY, poly = '';
    if (meets) {
      cutY = clipClamp(py1 - oy, 0, h);
      if (i === 0) {
        cutX = clipClamp(px0 - ox, 0, w);
        /* west of the sheet OR south of it */
        poly = 'polygon(0 0,' + cutX.toFixed(2) + 'px 0,' + cutX.toFixed(2) +
          'px ' + cutY.toFixed(2) + 'px,100% ' + cutY.toFixed(2) + 'px,100% 100%,0 100%)';
      } else {
        cutX = clipClamp(px1 - ox, 0, w);
        /* east of the sheet OR south of it */
        poly = 'polygon(' + cutX.toFixed(2) + 'px 0,100% 0,100% 100%,0 100%,0 ' +
          cutY.toFixed(2) + 'px,' + cutX.toFixed(2) + 'px ' + cutY.toFixed(2) + 'px)';
      }
    }
    if (poly !== bookClipKey[i]) {
      bookClipKey[i] = poly;
      cv.style.clipPath = poly;
    }
    out[i] = { clip: poly, covered: meets,
               cut: meets ? [+cutX.toFixed(2), +cutY.toFixed(2)] : null };
  }
  bookClipState = { mobile: false, fold: +O.foldT.toFixed(4), span: span, books: out };
}
/* ==== LB2 (2026-10-04): ON THE PHONE THE BOOKS ON THE DESK WEAR THEIR
   LABELS. Tommy: "isn't it also a little odd that in portrait view, we
   don't have the word journal on the journal". On the phone the two
   `.book-cover` buttons are the spines at the foot of the map (PO1), so off
   the map, folded or in the room, the books on the desk had no label either
   way up. Off the map each book gets a copy of its cover's face, laid on
   the drawn cover by the same affine the desktop's labels take
   (labelsAtPose) and cut where the paper lies over it as the desktop's
   are (clipBooksToPaper). It is type only: the cloth is the scene's. ==== */
var phoneLbl = [null, null];
function paperPoly(S, b, i) {
  var L = S.layout, span = { x: 0, y: 0, w: S.fld.sheet.w, h: S.fld.sheet.h };
  if (O.foldOn && O.foldT > 0.0005 && O.foldSpan) span = O.foldSpan();
  var px0 = L.ox + span.x * L.k, px1 = px0 + span.w * L.k;
  var py0 = L.oy + span.y * L.k, py1 = py0 + span.h * L.k;
  var ox = b.x, oy = b.y - b.z, w = b.w, h = b.h + b.z;
  if (!(px1 > ox && px0 < ox + w && py1 > oy && py0 < oy + h)) return '';
  var cutY = clipClamp(py1 - oy, 0, h), cutX;
  if (i === 0) {
    cutX = clipClamp(px0 - ox, 0, w);
    return 'polygon(0 0,' + cutX.toFixed(2) + 'px 0,' + cutX.toFixed(2) + 'px ' + cutY.toFixed(2) +
           'px,100% ' + cutY.toFixed(2) + 'px,100% 100%,0 100%)';
  }
  cutX = clipClamp(px1 - ox, 0, w);
  return 'polygon(' + cutX.toFixed(2) + 'px 0,100% 0,100% 100%,0 100%,0 ' + cutY.toFixed(2) + 'px,' +
         cutX.toFixed(2) + 'px ' + cutY.toFixed(2) + 'px)';
}
function phoneLabels(S) {
  if (!PHONE) return;
  var RB = window.RoomBooks, RP = window.RoomPhone, H = document.documentElement;
  /* off the map: upright that is the phone's room (an upright glass is the
     Journal's page otherwise, which RB.mapView counts as the map) */
  var off = (RP && RP.room && RP.room()) || (RB && RB.mapView && !RB.mapView());
  /* BV1 A2: and on the open sideways map, where the real books lie on the
     paper's foot: their names at their heads, and the covers are the hands' */
  /* BV2: and their labels stay on them on the way to the desk (the fold
     makes the glass `off` the map at once; the books are not off it yet) */
  var a2 = BK_A2 && S.layout && S.layout.side, a2m = a2 ? O.a2Out(S) : 1;
  if (a2 && a2m >= 1) a2 = false;
  /* TB2 (2026-10-06): a tablet on its side is laid as the desk (the whole
     sheet, S.wide past a half), and there the books beside the paper wear
     their names and take the hand as a desk's do: the label, cut where the
     paper lies over the cover, is what a tap lands on */
  var tdesk = TABLET && !off && !a2 && S.layout && S.layout.side && (S.layout.wide || 0) > 0.5;
  var show = !!((off || a2 || tdesk) && S.VP && S.camEye && window.Room && Room.scenePoint && Room.quadAff &&
                !H.classList.contains('bookout'));
  if (BK_A2) a2Hits(S, show && a2 && !off && a2m < 0.5);
  var bk = show ? O.books(S, true) : null, ids = ['key', 'journal'];
  for (var i = 0; i < 2; i++) {
    var el = phoneLbl[i];
    if (!show) { if (el && el.style.visibility !== 'hidden') el.style.visibility = 'hidden'; continue; }
    if (!el) {
      var src = document.querySelector('#' + ids[i] + ' .book-cover .book-face');
      if (!src) continue;
      el = document.createElement('div');
      el.className = 'desk-label' + (i ? ' desk-label--journal' : ''); el.setAttribute('aria-hidden', 'true');
      el.appendChild(src.cloneNode(true));
      document.body.appendChild(el);
      phoneLbl[i] = el;
      (function (el, id) {
        el.addEventListener('click', function () {
          var btn = document.querySelector('#' + id + ' .book-cover');
          if (btn && el.classList.contains('desk-label--hand')) btn.click();
        });
      })(el, ids[i]);
    }
    var b = bk[i], bw = b.w, bh = b.h + b.z, x0 = b.x, y0 = b.y - b.z, zc = b.z;
    var q = S.camEye[2] > zc + 0.01 ? [Room.scenePoint(x0, -y0, zc), Room.scenePoint(x0 + bw, -y0, zc),
             Room.scenePoint(x0 + bw, -(y0 + bh), zc), Room.scenePoint(x0, -(y0 + bh), zc)] : null;
    var t = q && q[0] && q[1] && q[2] && q[3] ? Room.quadAff(q, bw, bh) : null;
    if (!t) { el.style.visibility = 'hidden'; continue; }
    el.style.width = bw + 'px'; el.style.height = bh + 'px';
    el.style.setProperty('--bk-sp', b.sp + 'px');
    el.firstChild.style.height = b.h + 'px';
    el.style.transform = t;
    el.style.clipPath = a2 && a2m < 1 ? 'none' : paperPoly(S, b, i);
    el.classList.toggle('desk-label--head', a2 && a2m < 0.5);
    el.classList.toggle('desk-label--hand', !!tdesk);
    el.style.visibility = 'visible';
  }
}
/* BV1 A2: the two spine buttons are laid, unseen, over what shows of each
   cover on the glass (its four corners through the camera), so a tap on the
   real book is a tap on its button; off the open map they are given back
   to styles.css */
var a2Box = [null, null];
function a2Hits(S, on) {
  var bk = on ? O.books(S, true) : null, ids = ['key', 'journal'];
  for (var i = 0; i < 2; i++) {
    var btn = document.querySelector('#' + ids[i] + ' .book-cover');
    if (!btn) continue;
    var r = null;
    if (on) {
      var b = bk[i], y0 = b.y - b.z, xs = [], ys = [];
      [[b.x, y0], [b.x + b.w, y0], [b.x + b.w, y0 + b.h + b.z], [b.x, y0 + b.h + b.z]].forEach(function (c) {
        var p = Room.scenePoint(c[0], -c[1], b.z);
        if (p) { xs.push(p[0]); ys.push(p[1]); }
      });
      if (xs.length === 4) {
        var L = S.layout, x0 = Math.max(0, Math.min.apply(0, xs)), x1 = Math.min(L.cw, Math.max.apply(0, xs));
        var t0 = Math.max(0, Math.min.apply(0, ys)), t1 = Math.min(L.ch, Math.max.apply(0, ys));
        if (x1 - x0 > 8 && t1 - t0 > 8) r = [x0, t0 - 8, x1 - x0, t1 - t0 + 8];   /* a finger's slack above the head */
      }
    }
    var key = r ? r.map(Math.round).join(',') : '';
    if (key === a2Box[i] && (!r || btn.style.width)) continue;   /* placeBooks clears them on a refit */
    a2Box[i] = key;
    var st = btn.style;
    if (r) { st.left = r[0] + 'px'; st.top = r[1] + 'px'; st.width = r[2] + 'px'; st.height = r[3] + 'px'; st.bottom = 'auto'; }
    else st.left = st.top = st.width = st.height = st.bottom = '';
    btn.classList.toggle('a2-hit', !!r);
  }
}
O.a2Hits = function () { return a2Box.slice(); };
/* Read-only evidence hook: it reports the same cuts written to the two
   real buttons, without doing geometry or forcing layout. */
O.bookInput = function () { return bookClipState; };

/* the DOM keeps the labels for now (E6 owns the open books), so the box
   they are set in has to be where the cover is drawn */
function placeBooks(S) {
  if (NO_BOOKS) return;
  wireLift();
  var bk = O.books(S, true), ids = ['key', 'journal'], MOBILE_BARS = mobileBars();
  for (var i = 0; i < 2; i++) {
    var n = document.getElementById(ids[i]);
    if (!n) continue;
    if (MOBILE_BARS) {                       /* R67: the bars keep their own layout */
      n.style.left = n.style.top = n.style.right = n.style.bottom = '';
      var cv0 = n.querySelector('.book-cover');
      if (cv0) { cv0.style.width = cv0.style.height = ''; cv0.style.removeProperty('--bk-sp'); }
      var fc0 = n.querySelector('.book-face');
      if (fc0) fc0.style.height = '';
      continue;
    }
    n.style.left = bk[i].x + 'px';
    n.style.top = (bk[i].y - bk[i].z) + 'px';
    n.style.right = 'auto'; n.style.bottom = 'auto';
    /* R63: THE LABEL IS GLUED TO THE COVER, so the box that holds it is
       the cover's own. styles.css keeps 9.4rem x (6.4rem + 15px) as the
       default — which is (BK_W, BK_H + BK_Z) to the pixel — and these
       three properties write the cover's own. The paper label inside it is inset from the
       spine, so the spine goes with them (--bk-sp); its TYPE does not
       scale, because a bigger book does not get bigger lettering on its
       label, it gets a bigger label. */
    var cv = n.querySelector('.book-cover');
    if (cv) {
      cv.style.width = bk[i].w + 'px';
      cv.style.height = (bk[i].h + bk[i].z) + 'px';
      cv.style.setProperty('--bk-sp', bk[i].sp + 'px');
      var fc = n.querySelector('.book-face');
      if (fc) fc.style.height = bk[i].h + 'px';
      /* L: the leaf's back wears the same label in a box the cover's own
         size (styles.css .book-hinge-cover) */
      n.style.setProperty('--bk-w', bk[i].w + 'px');
      n.style.setProperty('--bk-h', bk[i].h + 'px');
      n.style.setProperty('--bk-sp', bk[i].sp + 'px');
    }
  }
  clipBooksToPaper(S, bk);
}
/* R17 (c): THE TWO LABELS ARE DOM AND DOM DOES NOT COME WITH THE CAMERA.
   At the edge pose the covers are no longer the rect css says they are, so
   the flat box was MAPPED onto the projected one: the origin went where
   the cover's own origin goes and the scale was the projected width over
   the real one.

   R30 — AND A COVER AT 22 DEGREES IS NOT A RECTANGLE. Tommy: "things like
   the books are a bit askew, like the text labels on them." An origin and
   ONE scale is the weakest plate of the three the desk had: it cannot
   turn, it cannot foreshorten one axis against the other, and it takes
   its scale at the cover's NORTH edge and applies it to the whole board —
   so the south of the label, which is nearer the eye and should be wider,
   came out narrow and walked east off the cloth. Measured at the shipped
   pose, 1536 x 864 at devicePixelRatio 1.75, against where the camera
   actually puts the cover's four corners:

     the cover box  3.53, 2.87, 27.43, 21.88 device px out (NW, NE, SW, SE)
     the paper label 15.91, 13.60, 26.29, 22.23

   A three-point affine — the shape `Room.onPaper` makes — takes the first
   three to zero and leaves 4.90 device px on the cover's fourth corner
   (1.74 on the label's). The full projective transform takes all four to
   zero, and it is the same four points either way, so it is the one built:
   `Room.quadMat` (engine.js) off the cover's own corners on its own plane
   at z = BK_Z.

   IT IS WRITTEN ON `.book-cover`, NOT ON THE `.book` BOX. The cover is the
   thing standing on the desk; the `.book-body` panel beside it is not, and
   the stylesheet has said so since v9 (`.book[data-open="true"] {
   transform: none }`) — a rule an inline transform on the aside quietly
   beat, so an open book's leaves were being laid on the desk's plane too.
   The hit box goes with the cloth, which is where the hand should find it.

   Still a transform, so the type stays type and nothing is relaid out;
   still written only when it has moved, because a DOM write a frame is a
   DOM write a frame. The lift (`liftLabels`, BOOK_OUT) stays on
   `.book-face` INSIDE this transform, so the ten pixels a picked-up book
   travels are now carried in the cover's own perspective instead of the
   screen's. */
function labelsAtPose(S) {
  var L = S.layout, bk = O.books(S, true), ids = ['key', 'journal'];
  var zc = BK_Z;                              /* R63: the cover being laid */
  var laid = [false, false];
  function scr(x, y) {
    return Room.scenePoint(x, -y, zc);
  }
  if (!O._lblKey) O._lblKey = ['', ''];
  for (var i = 0; i < 2; i++) {
    var n = document.getElementById(ids[i]); if (!n) continue;
    var cv = n.querySelector('.book-cover'); if (!cv) continue;
    /* the aside carried the old transform: give it back to the stylesheet */
    if (n.style.transform) { n.style.transform = ''; n.style.transformOrigin = ''; }
    var b0 = bk[i]; zc = b0.z;              /* R63 */
    var x0 = b0.x, y0 = b0.y - b0.z;
    /* the button's own box: the cover, plus the fifteen px of lead above
       it that holds the lifted state (styles.css .book-cover). Read ONCE
       per window — offsetWidth is a layout read and this runs in draw(). */
    /* 2026-09-20 (Tommy: the Journal's label "kind of sits over the
       map-paper"): THE BOX IS THE COVER'S OWN SIZE, WHICH placeBooks
       WRITES — not offsetWidth read once per window. On a cold boot this
       ran before placeBooks had written the R63 size for the window, so
       the cache held the stylesheet's default (150 x 117) and the plate
       was fitted to that patch of the journal's 287 x 472 cover: an
       affine struck on the top quarter of a trapezoid carries the wrong
       vertical scale over the rest, and the label landed 28 px short of
       the drawn foot at 1537x865 (face foot 755, hull foot 783), against
       the sheet's draped edge. A resize placed first and measured 0 px
       off, which is how it hid. The desk numbers are the button's numbers
       (placeBooks), so they are used and the cache is keyed on them; the
       phone's bars keep their own layout and are still read. */
    var wk = L.cw + 'x' + L.ch + '|' + b0.w + 'x' + (b0.h + b0.z);
    if (!O._lblBox) O._lblBox = [null, null];
    if (!O._lblBox[i] || O._lblBox[i].k !== wk)
      O._lblBox[i] = { k: wk, w: mobileBars() ? (cv.offsetWidth || b0.w) : b0.w,
                       h: mobileBars() ? (cv.offsetHeight || (b0.h + b0.z)) : b0.h + b0.z,
                       /* Desktop is deliberately the old exact origin.
                          Mobile bars need their actual parent origin. */
                       ox: mobileBars() ? n.offsetLeft : x0,
                       oy: mobileBars() ? n.offsetTop : y0 };
    var bw = O._lblBox[i].w, bh = O._lblBox[i].h;
    /* Front-cover ink cannot show through the back board. */
    if (!S.camEye || S.camEye[2] <= zc + 0.01) continue;
    var q = [scr(x0, y0), scr(x0 + bw, y0), scr(x0 + bw, y0 + bh), scr(x0, y0 + bh)];
    /* THE BUTTON IS NOT AT THE WINDOW'S ORIGIN. Everything else laid on
       this desk is an absolute box at left 0, top 0, so its matrix is
       absolute; the cover sits inside a `.book` that css has already put
       at (x0, y0), and a transform is applied on top of that. So the quad
       is taken RELATIVE to where the element already is. */
    for (var c = 0; c < 4; c++) if (q[c]) {
      q[c][0] -= O._lblBox[i].ox; q[c][1] -= O._lblBox[i].oy;
    }
    var t = LBL_3D ? Room.quadMat(q, bw, bh) : Room.quadAff(q, bw, bh);
    if (!t) continue;
    laid[i] = true;
    if (O._lblKey[i] === t) continue;
    O._lblKey[i] = t;
    cv.style.transformOrigin = '0 0';
    cv.style.transform = t;
  }
  return laid;
}
/* ==== 2026-09-19: THE HAND FINDS THE BOOK WHEREVER THE BOOK IS DRAWN ====
   Tommy: "what is the click box for the field guide and journal? it
   really doesn't feel massively clickable, there's areas at the bottom,
   even unhovered, that aren't clickable".

   It was the cover: `.book-cover`, the label's plate, laid on the TOP
   FACE at the cover's height (labelsAtPose) as an affine — so (1) the
   affine is a parallelogram and the face at the standing eye is a
   trapezoid, wider at its near edge, and the near corners fell outside;
   (2) the FORE-EDGE and the page block under the cover — nearest the
   eye, lowest on the screen — were drawn and were in no button at all;
   (3) the lift carried the solid ten px out and two up and the plate
   followed by a translate, so the two silhouettes never quite agreed.

   Now one transparent box a book, `.book-hit`, laid every frame on the
   HULL of the drawn solid: the far edge of the top face at the cover's
   height (with the lift's rise) down to the near edge of the footprint on
   the wood, four corners through the same `scr()` the labels use, as the
   exact projective plate (`Room.quadMat`) — so it is right in every
   pose, lifted or not. The paper still owns the hand where it covers the
   book: the same two lines clipBooksToPaper cuts the cover by (the
   sheet's edge on this book's side and its southern edge, on the cover's
   plane) are carried through the plate's INVERSE into the box's own
   coordinates and cut there, a straight line to a straight line. The
   cover keeps its plate, its clip, its label and its focus; the hit box
   lies over it and forwards the hand (wireLift). `?hit=0` is the before. */
function bookHitEl(bk) {
  var hit = bk.querySelector('.book-hit');
  if (hit) return hit;
  hit = document.createElement('div');
  hit.className = 'book-hit';
  hit.setAttribute('aria-hidden', 'true');
  bk.appendChild(hit);
  return hit;
}
/* the solid's eight corners on the screen, and the four the box is laid
   by: TL, TR of the top face, BR, BL of the base. Screen css px, absolute;
   `origin` is where css has put the `.book` aside the box lives in. */
function bookHullOf(S, i, b, raw) {
  var rise = BOOK_RISE * LIFT[i].v, zt = b.z + rise;
  function P(x, y, z) { return Room.scenePoint(x, -y, z); }
  var top = [P(b.x, b.y, zt), P(b.x + b.w, b.y, zt), P(b.x + b.w, b.y + b.h, zt), P(b.x, b.y + b.h, zt)];
  var base = [P(b.x, b.y, rise), P(b.x + b.w, b.y, rise), P(b.x + b.w, b.y + b.h, rise), P(b.x, b.y + b.h, rise)];
  return { top: top, base: base, quad: [top[0], top[1], base[2], base[3]],
           w: b.w, h: b.h + b.z, zt: zt, origin: [raw.x, raw.y - raw.z] };
}
/* Read-only evidence hook: the same corners the box is laid by. */
O.bookHull = function (S) {
  S = S || O.S;
  if (!S || !S.layout || !S.fld || !S.VP) return null;
  var bk = O.books(S), raw = O.books(S, true);
  return [bookHullOf(S, 0, bk[0], raw[0]), bookHullOf(S, 1, bk[1], raw[1])];
};
/* the inverse of the plate `Room.quadMat` writes for (q, w, h): screen
   (relative to the element's origin) back to the box's own (u, v) */
function quadInv(q, w, h) {
  var x0 = q[0][0], y0 = q[0][1], x1 = q[1][0], y1 = q[1][1],
      x2 = q[2][0], y2 = q[2][1], x3 = q[3][0], y3 = q[3][1];
  var sx = x0 - x1 + x2 - x3, sy = y0 - y1 + y2 - y3, a, b, d, e, g = 0, hh = 0;
  if (Math.hypot(sx, sy) < 0.25) {            /* quadMat's own flat case */
    a = (x1 - x0) / w; d = (y1 - y0) / w; b = (x3 - x0) / h; e = (y3 - y0) / h;
  } else {
    var ax1 = x1 - x2, ax2 = x3 - x2, ay1 = y1 - y2, ay2 = y3 - y2, den = ax1 * ay2 - ax2 * ay1;
    if (Math.abs(den) < 1e-12) return null;
    g = (sx * ay2 - ax2 * sy) / den; hh = (ax1 * sy - sx * ay1) / den;
    a = (x1 - x0 + g * x1) / w; d = (y1 - y0 + g * y1) / w;
    b = (x3 - x0 + hh * x3) / h; e = (y3 - y0 + hh * y3) / h;
    g /= w; hh /= h;
  }
  return function (X, Y) {
    var m00 = a - g * X, m01 = b - hh * X, m10 = d - g * Y, m11 = e - hh * Y;
    var r0 = X - x0, r1 = Y - y0, det = m00 * m11 - m01 * m10;
    if (Math.abs(det) < 1e-12) return null;
    return [(r0 * m11 - m01 * r1) / det, (m00 * r1 - m10 * r0) / det];
  };
}
var hitKey = ['', ''], hitState = [null, null];
function hitWrite(i, hit, t, poly, bw, bh) {
  var key = t ? t + '|' + poly + '|' + bw + 'x' + bh : '';
  if (hitKey[i] === key) return;
  hitKey[i] = key;
  hit.style.display = t ? 'block' : 'none';
  /* the box the plate is struck for: the book's own width, its depth and
     its thickness — the same box as the cover's (placeBooks) */
  hit.style.width = t ? bw + 'px' : '';
  hit.style.height = t ? bh + 'px' : '';
  hit.style.transform = t || '';
  hit.style.clipPath = t ? poly : '';
}
function hitsAtPose(S) {
  if (!HIT_ON || !liftHits[0] && !liftHits[1]) return;
  var L = S.layout, bars = mobileBars() || !S.VP || !S.camEye;
  var bk = bars ? null : O.books(S), raw = bars ? null : O.books(S, true);
  var span = { x: 0, y: 0, w: S.fld.sheet.w, h: S.fld.sheet.h };
  if (!bars && O.foldOn && O.foldT > 0.0005 && O.foldSpan) span = O.foldSpan();
  var px0 = L.ox + span.x * L.k, px1 = px0 + span.w * L.k;
  var py0 = L.oy + span.y * L.k, py1 = py0 + span.h * L.k;
  for (var i = 0; i < 2; i++) {
    var hit = liftHits[i]; if (!hit) continue;
    if (bars) { hitWrite(i, hit, '', ''); hitState[i] = null; continue; }
    var b = bk[i], H = bookHullOf(S, i, b, raw[i]), q = H.quad;
    var ox = H.origin[0], oy = H.origin[1], bw = H.w, bh = H.h;
    /* the cover cannot be hit through the back board */
    if (S.camEye[2] <= H.zt + 0.01 || !q[0] || !q[1] || !q[2] || !q[3]) {
      hitWrite(i, hit, '', ''); hitState[i] = null; continue;
    }
    var rq = [[q[0][0] - ox, q[0][1] - oy], [q[1][0] - ox, q[1][1] - oy],
              [q[2][0] - ox, q[2][1] - oy], [q[3][0] - ox, q[3][1] - oy]];
    var t = Room.quadMat(rq, bw, bh);
    if (!t) { hitWrite(i, hit, '', ''); hitState[i] = null; continue; }
    var poly = '', cut = null;
    var meets = px1 > b.x && px0 < b.x + b.w && py1 > b.y - b.z && py0 < b.y + b.h;
    var inv = meets ? quadInv(rq, bw, bh) : null;
    if (inv) {
      /* the paper's two edges over this book, on the cover's plane, in
         the box's own coordinates */
      var nat = function (x, y) {
        var p = Room.scenePoint(x, -y, H.zt);
        return p ? inv(p[0] - ox, p[1] - oy) : null;
      };
      var ex = i ? px1 : px0;                 /* the sheet's edge on this side */
      var A = nat(b.x, py1), B = nat(b.x + b.w, py1), C = nat(ex, b.y), D = nat(ex, py1);
      if (A && B && C && D) {
        var du = B[0] - A[0], mC = Math.abs(du) > 1e-6 ? (B[1] - A[1]) / du : 0;
        var dv = D[1] - C[1], mW = Math.abs(dv) > 1e-6 ? (D[0] - C[0]) / dv : 0;
        var vL = A[1] - A[0] * mC, vR = A[1] + (bw - A[0]) * mC;
        var uT = C[0] - C[1] * mW, den = 1 - mC * mW;
        var uX = Math.abs(den) > 1e-6 ? (C[0] + (A[1] - A[0] * mC - C[1]) * mW) / den : C[0];
        var vX = A[1] + (uX - A[0]) * mC;
        vL = clipClamp(vL, 0, bh); vR = clipClamp(vR, 0, bh); vX = clipClamp(vX, 0, bh);
        uT = clipClamp(uT, 0, bw); uX = clipClamp(uX, 0, bw);
        var f = function (v) { return v.toFixed(2) + 'px'; };
        /* HB1 (2026-09-22): THE CURLED CORNER IS NOT PAPER OVER THE BOOK.
           The sheet's south-east corner is rolled up off the journal
           (objects.js SE_CURL), and what the roll uncovers is the journal's
           board, plainly in view — but the cut above took the paper's
           outline as if the corner lay flat, so a press on the board seen
           under the curl landed on nothing. Tommy: "the raised bottom right
           sheet corner is journal but not clickable to open the journal".
           The corner comes off the cut along the drawing's own crease:
           the sheet shader rolls every station with q > 0, where q is the
           SUM of the two distances past (edge - curl) (objects.js R45,
           uFold.x), so the crease meets each edge 2 * curl from the corner
           — measured on his Chrome, the drawn crease's legs are 101 css
           px where curl is 50.6. The same number, the same css px, so the
           hit and the picture agree. Only the journal's corner curls, and
           only while the sheet is flat. */
        var cS = null, cE = null;
        if (i === 1 && !(O.foldOn && O.foldT > 0.0005)) {
          var curl = 2 * Math.min(64, S.fld.sheet.w * 0.045) * L.k;
          if (px1 - curl > b.x && py1 - curl > b.y - b.z) { cS = nat(px1 - curl, py1); cE = nat(px1, py1 - curl); }
        }
        poly = i === 0
          /* west of the sheet OR south of it */
          ? 'polygon(0 0,' + f(uT) + ' 0,' + f(uX) + ' ' + f(vX) + ',100% ' + f(vR) + ',100% 100%,0 100%)'
          /* east of the sheet OR south of it, the rolled corner given back */
          : (cS && cE
            ? 'polygon(' + f(uT) + ' 0,100% 0,100% 100%,0 100%,0 ' + f(vL) + ',' +
              f(clipClamp(cS[0], 0, bw)) + ' ' + f(clipClamp(cS[1], 0, bh)) + ',' +
              f(clipClamp(cE[0], 0, bw)) + ' ' + f(clipClamp(cE[1], 0, bh)) + ')'
            : 'polygon(' + f(uT) + ' 0,100% 0,100% 100%,0 100%,0 ' + f(vL) + ',' + f(uX) + ' ' + f(vX) + ')');
        cut = { uT: +uT.toFixed(2), uX: +uX.toFixed(2), vX: +vX.toFixed(2), vL: +vL.toFixed(2), vR: +vR.toFixed(2),
                curl: cS && cE ? [+cS[0].toFixed(1), +cS[1].toFixed(1), +cE[0].toFixed(1), +cE[1].toFixed(1)] : null };
      }
    }
    if (inv && L.books === 'below') {         /* PH10: as clipBooksToPaper */
      var E1 = Room.scenePoint(px1, -(b.y - b.z), H.zt), E2 = Room.scenePoint(px1, -(b.y + b.h), H.zt);
      E1 = E1 && inv(E1[0] - ox, E1[1] - oy); E2 = E2 && inv(E2[0] - ox, E2[1] - oy);
      if (E1 && E2) poly = 'polygon(' + clipClamp(E1[0], 0, bw).toFixed(2) + 'px 0,100% 0,100% 100%,' +
                           clipClamp(E2[0], 0, bw).toFixed(2) + 'px 100%)';
    }
    hitWrite(i, hit, t, poly, bw, bh);
    hitState[i] = { plate: t, clip: poly, covered: meets, cut: cut, quad: q, box: [bw, bh], origin: [ox, oy] };
  }
}
/* Read-only evidence hook: the plate and the cut written to the two hit boxes. */
O.bookHit = function () { return hitState.slice(); };
/* ==== BK1: AND THE OPEN BOOK TAKES THE SAME PLATE ====================
   The panel is the one thing on this desk that is DOM and is not a label,
   and until now it was the one thing that never went through the camera:
   at the desk pose it stood flat on the glass over a desk at 53 degrees,
   and in the room it was hidden and a different book was drawn instead.
   It is laid on the desk now, by its own four corners, exactly as R17 (c)
   and R30 lay the covers — and the reader in the room sees the journal
   lying where the journal is.

   IT TAKES THE MATRIX3D AND NOT THE AFFINE. R30 measured both on the two
   labels and shipped the affine there, because a matrix3d gives an
   element its own layer and Chrome rasters that layer once: on type
   minified to 0.55 the edge loses. The measurement does not carry across
   to this plate, because the residual an affine leaves is a fraction of
   the WHOLE QUAD — on a 150 px label it was under a css pixel, and this
   panel is 965 px wide, so the same fraction is tens of pixels of skew in
   a rectangle the eye reads as a physical object lying flat. Shape wins
   here; `?lbl=3d` still picks the labels' pair, as it always has.

   ONLY WHEN IT HAS LANDED, and never at the rest pose (there the css
   placement IS the desk placement, so nothing is written and the fit is
   the fit). The plate is cleared the moment the book starts to shut, and
   the hinge takes over as the representation. */
/* Room.quadAff's six lines, as numbers: the least-squares affine of the
   cover's quad (the button's box, with its lead, at the cover's height,
   exactly labelsAtPose's), applied to the canonical two-width box's four
   corners at the carried pose. Null off the map view or without a camera. */
function coverAffineQuad(S, i, b, p, z) {
  if (!O._lblBox || !O._lblBox[i]) return null;
  var bw = O._lblBox[i].w, bh = O._lblBox[i].h, x0 = b.x, y0 = b.y - b.z;
  function scr(x, y) { return Room.scenePoint(x, -y, b.z); }
  var q = [scr(x0, y0), scr(x0 + bw, y0), scr(x0 + bw, y0 + bh), scr(x0, y0 + bh)];
  if (!q[0] || !q[1] || !q[2] || !q[3]) return null;
  var ax = ((q[1][0] - q[0][0]) + (q[2][0] - q[3][0])) / (2 * bw),
      ay = ((q[1][1] - q[0][1]) + (q[2][1] - q[3][1])) / (2 * bw),
      cx = ((q[3][0] - q[0][0]) + (q[2][0] - q[1][0])) / (2 * bh),
      cy = ((q[3][1] - q[0][1]) + (q[2][1] - q[1][1])) / (2 * bh);
  var mx = (q[0][0] + q[1][0] + q[2][0] + q[3][0]) / 4, my = (q[0][1] + q[1][1] + q[2][1] + q[3][1]) / 4;
  var tx = mx - (ax * bw + cx * bh) / 2, ty = my - (ay * bw + cy * bh) / 2;
  /* the box's corners in the button's own coordinates: the button's box
     starts one thickness north of the cover, so the top face at height z
     is at v = b.z + (z - b.z), i.e. the same plane when z is the cover's */
  var pts = [[p.x, p.y], [p.x + p.w, p.y], [p.x + p.w, p.y + p.h], [p.x, p.y + p.h]];
  return pts.map(function (pt) {
    var u = pt[0] - x0, v = pt[1] - y0 - (z - b.z);
    return [ax * u + cx * v + tx, ay * u + cy * v + ty];
  });
}
function panelsAtPose(S) {
  /* SO2 (2026-10-01): the block is drawn where the HAND has it (O.books
     with the hover lift), so the panel and the leaf's label are laid there
     too. Laid at the resting place they stood the hover's 10 px up-left of
     the cover's own label on the click that opened the book, and the label
     jumped as one handed over to the other. At rest the two are the same.
     The ORIGIN the plate is taken relative to stays the resting one
     (`bkR`): that is where css has put the element. */
  var bk = O.books(S), bkR = O.books(S, true), dim = bookDims(S), bars = mobileBars();
  /* HOW FAR THE READER HAS LEFT THE PAPER. Nothing is written at the map
     view's own poses ONCE THE BOOK HAS LANDED — the fit, a tilt, a zoom,
     the desk pose — so the reader Tommy has, laid out by css, is BYTE FOR
     BYTE the reader he keeps: "when you're in the map view, the book goes
     over the map". Standing up and turning to the machine are the two
     moves that take the desk away, and each has its own eased channel, so
     the plate comes in on the move itself and there is no jump at either
     end: at k = 0 the panel is where css puts it and at k = 1 it is where
     the camera puts a thing of that size lying on the desk. No new easing,
     no new timer — this is the camera's own.

     ==== L: AND FROM THE FIRST FRAME OF THE OPEN. Tommy: "the book opens
     up to 2 blank pages and then our actual book overlay pops over the
     top with the book underneath disappearing". The panel is laid on the
     block at every stage but `closed`: its four corners are the block's
     top face at the carried pose (bookPose, bookTarget), through the
     camera, plus the fit's residual carried in with the carry — so at
     carry 0 the panel's near half is the shut cover to the pixel, and at
     carry 1 in the map view the plate is the identity and is NOT written,
     exactly as before. Under the room's camera (k > 0) it is the same mix
     by k it was. */
  var k = Math.max(S.cam.room || 0, S.cam.lap || 0);
  if (!O._pnlKey) O._pnlKey = ['', ''];
  if (!O._leafKey) O._leafKey = ['', ''];
  for (var i = 0; i < 2; i++) {
    var el = panelEl(i), stage = bookStageOf(i);
    var settled = stage === 'reading';
    var out = el && !!stage && stage !== 'closed';
    if (!el || !out || bars || !S.VP || !S.camEye) {
      if (el && O._pnlKey[i]) { O._pnlKey[i] = ''; el.style.transform = ''; el.style.transformOrigin = ''; }
      if (el && !i) { var inn = el.querySelector('.book-inner'); if (inn && inn.style.height) inn.style.height = ''; }
      if (el && el._jb1 && (bars || !stage)) { el._jb1 = ''; el.style.marginLeft = el.style.marginRight = el.style.marginBottom = ''; }   /* JB1 */
      /* At 820 px and under the panel is a sheet along the foot of the
         screen with no desk behind it (R67), so it takes no plate — but
         it is still the reader. */
      panelLaid[i] = settled && !!el;
      continue;
    }
    /* the box is read whenever the panel is out: its height is static
       now (styles.css, no max-height fade), so the first frame of the
       open can read it */
    var box = panelBox(S, i, out);
    if (!box || !(box.w > 0) || !(box.h > 0)) { panelLaid[i] = settled; continue; }
    var b = bk[i], p = bookPose(S, i, b, dim[i]);
    /* JB1: the css box moves in by the same pair the block does */
    var off = panelOff(S, i, box), mk = off[0].toFixed(1) + ',' + off[1].toFixed(1);
    if (el._jb1 !== mk) {
      el._jb1 = mk;
      el.style.marginLeft = i ? '' : off[0] + 'px';
      el.style.marginRight = i ? (-off[0]) + 'px' : '';
      el.style.marginBottom = (-off[1]) + 'px';
    }
    /* the key's inner is held at the height aspect-ratio gives it (4:3 of
       its width) while the halves cut their own pages (styles.css) */
    /* 2026-09-20: AND 2:3 IN A PORTRAIT WINDOW, which is the aspect
       styles.css gives the one-leaf inner there. Held at 4:3 it stood a
       third as tall as the leaf books.js had dealt against the closed
       inner's own height, and the page scrolled (measured at 900x1100:
       inner 344 px under a 1188 px leaf). */
    if (!i) {
      var inner = el.querySelector('.book-inner');
      var tall = window.matchMedia && window.matchMedia('(orientation: portrait)').matches;
      var ih = ((box.w - 22) * (tall ? 1.5 : 0.75)).toFixed(2) + 'px';
      if (inner && inner.style.height !== ih) inner.style.height = ih;
    }
    /* the label on the leaf's back is set at the cover's own size and
       scaled to the leaf's (styles.css .book-hinge-cover) */
    var lk = (box.w / 2 / b.w).toFixed(5) + ',' + (box.h / b.h).toFixed(5);
    if (O._leafKey[i] !== lk) {
      O._leafKey[i] = lk;
      var aside = document.getElementById(i ? 'journal' : 'key');
      if (aside) {
        aside.style.setProperty('--leaf-sx', (box.w / 2 / b.w).toFixed(5));
        aside.style.setProperty('--leaf-sy', (box.h / b.h).toFixed(5));
      }
    }
    /* the block's top: the lift on to the paper plus the whole shut block,
       which is where the near page of an open book actually is */
    var z = p.z + dim[i].z * p.scale;
    var bR = bkR[i];
    var x0 = (i ? (bR.x + bR.w - box.w) : bR.x) + off[0], y0 = bR.y + bR.h - box.h + off[1];
    if (S.camEye[2] <= z + 0.01) {      /* the eye is under the desk's own plane */
      if (O._pnlKey[i]) { O._pnlKey[i] = ''; el.style.transform = ''; el.style.transformOrigin = ''; }
      panelLaid[i] = false;
      continue;
    }
    /* landed, in the map view: the identity, and nothing is written */
    if (!(k > 0.0005) && p.carry >= 1 && p.res && !LEAF_PERSP) {
      if (O._pnlKey[i]) { O._pnlKey[i] = ''; el.style.transform = ''; el.style.transformOrigin = ''; }
      panelLaid[i] = settled;
      continue;
    }
    var q = [Room.scenePoint(p.x, -p.y, z), Room.scenePoint(p.x + p.w, -p.y, z),
             Room.scenePoint(p.x + p.w, -(p.y + p.h), z), Room.scenePoint(p.x, -(p.y + p.h), z)];
    if (!q[0] || !q[1] || !q[2] || !q[3]) { panelLaid[i] = settled; continue; }
    /* THE FIRST FRAME IS THE LABEL'S OWN PLATE. The shut cover's label is
       laid by the labels' AFFINE (labelsAtPose, Room.quadAff: the
       least-squares fit of the cover's four corners), and a projective
       plate of the same four corners is not that — at the label, on the
       cover's south, it is three per cent wider (measured: the label 141
       px wide at rest, 146 on the first frame of the open). So at carry 0
       the panel's corners are the SAME affine, struck here in numbers on
       the same cover quad and applied to the two-width box, and the
       perspective comes in with the carry. Only in the map view (k = 0):
       under the room's camera the labels are affine too, but the panel
       has always been projective there (BK1) and stays so. */
    var qa = (!(k > 0.0005) && p.carry < 1) ? coverAffineQuad(S, i, b, p, z) : null;
    /* the camera's four corners, the fit's residual carried in by the
       carry and out again by k; the quad is taken RELATIVE to where css
       has already put the element */
    var m = LEAF_PERSP ? 0 : p.carry * (1 - k), r = p.res;
    for (var c = 0; c < 4; c++) {
      if (qa) { q[c] = [qa[c][0] + (q[c][0] - qa[c][0]) * p.carry, qa[c][1] + (q[c][1] - qa[c][1]) * p.carry]; }
      q[c] = [q[c][0] - x0 + (r ? r[c][0] * m : 0),
              q[c][1] - y0 + (r ? r[c][1] * m : 0)];
    }
    var t = Room.quadMat(q, box.w, box.h);
    if (!t) { panelLaid[i] = settled; continue; }
    panelLaid[i] = settled;
    if (O._pnlKey[i] === t) continue;
    O._pnlKey[i] = t;
    el.style.transformOrigin = '0 0';
    el.style.transform = t;
  }
}
/* ==== R67 (c): WHICH BOOK IS OUT ======================================
   R67's watch on leaving the desk (`Room.mapLive()` once a frame) went
   with BK1: nothing is hidden when the reader leaves any more, because
   the panel is the book wherever the reader stands. What is left of it is
   the question the smoke hook asks. */
function openBookIdx() {
  var B = window.RoomBooks;
  if (!B || !B.open) return -1;
  var o = B.open();
  return o === 'key' ? 0 : o === 'journal' ? 1 : -1;
}
function drawBooks(gl, ctx) {
  if (!O.pBook) return;
  var S = ctx.S;
  /* Folding and the hover lift can move the physical relationship without
     a resize/place pass. Keep the two hit clips on that same frame. */
  clipBooksToPaper(S, O.books(S, true));
  /* The labels are DOM but their cloth is in the room. Carry their cover
     boxes through the same projective matrix whenever the camera leaves
     the original desk pose; at rest the established CSS placement remains
     byte-for-byte unchanged. */
  /* R17 (c): "the camera has left the desk" is measured from the chosen
     REST pose (S.cam.p0/k0). */
  var p0 = S.cam.p0 || 0, k0 = S.cam.k0 || 1;
  var off = S.cam.pitch - p0 > 0.01 || Math.abs(S.cam.k - k0) > 0.01 || S.cam.room > 0.001;
  O._labelsOff = off;
  var laid = ((O.R17.edge || off) && S.VP && !PHONE) ? labelsAtPose(S) : [true, true];
  phoneLabels(S);                                     /* LB2 */
  /* 2026-09-19: and the hit box is laid on the whole solid EVERY frame,
     at the rest pose too — the cover's flat box was never the solid. */
  hitsAtPose(S);
  /* BK1: and the open panel is laid on the desk by the same camera —
     but on its own test, not the labels'. R17 (c)'s `off` is "the camera
     has left the REST pose", which is true at every tilt and zoom of the
     map view, and the map view's reader is the one thing this card must
     not move. `S.cam.room` and `S.cam.lap` are the two moves that take
     the desk away, and they are what the panel rides. */
  panelsAtPose(S);
  /* A shut book keeps its label unless its real face cannot be projected.
     ==== BK1: AND AN OPEN BOOK KEEPS ITS PANEL EVERYWHERE. R67 hid the
     whole `.book` aside while the reader was away, because the physical
     spread was standing in for it at the rear station; the panel IS the
     book now, in every view, so what is hidden is the COVER — which is
     the only thing in that aside a projection can fail for. (An open
     book's cover is already invisible: styles.css fades `.book-face` to
     nothing the moment `data-open` is true.) */
  if (!O._labHid) O._labHid = [null, null];
  var hid0 = !laid[0], hid1 = !laid[1];
  if (hid0 !== O._labHid[0] || hid1 !== O._labHid[1]) {
    O._labHid[0] = hid0; O._labHid[1] = hid1;
    var hh = [hid0, hid1];
    ['key', 'journal'].forEach(function (id, i) {
      var n = document.getElementById(id);
      var cv = n && n.querySelector('.book-cover');
      if (n && n.style.visibility) n.style.visibility = '';
      if (cv) cv.style.visibility = hh[i] ? 'hidden' : '';
    });
  }
  var L = ctx.L, s = ctx.sun, p = O.pBook, bk = O.books(S), dim = bookDims(S);
  /* R7: A BOUND BOOK OCCLUDES ITSELF. E4's five quads could be ordered by
     hand (the cover last); the boards, the recess and the round spine
     cannot, so the two books and the sheet after them are drawn against
     the depth buffer and it is put back the way it was found — the frame
     clears it and the ground's own pass wants it clean. */
  ctx.depthFore(gl);
  gl.useProgram(p);
  gl.uniformMatrix4fv(p.u.uVP, false, S.VP);
  gl.uniform3f(p.u.uSheet, L.ox, L.oy, L.k);
  gl.uniform3f(p.u.uLight, s.L[0], s.L[1], s.L[2]);
  var eb = ctx.eye;
  gl.uniform3f(p.u.uEye, eb[0], eb[1], eb[2]);
  gl.uniform1f(p.u.uSpec, ctx.spec);
  /* R63: uZpx is the cover's thickness in device px and the two covers
     no longer share one — it is written per book, below. */
  /* R15: the sheet is lying on these covers, so its edge shades them */
  var f15 = S.fld;
  gl.uniform4f(p.u.uPaper, 0, 0, f15.sheet.w, f15.sheet.h);
  /* R45: the cast in the notch is the ROLL's now, so the height it is
     thrown from is the roll's own — two thirds of the tip, which is
     about where the paper that is actually over the notch stands. */
  var cu15 = Math.min(64, f15.sheet.w * 0.045);
  var contact = 0.34;
  /* ==== BK1: AND THE SHEET IS NOT LYING ON AN OPEN BOOK ==============
     R15's contact line, R45's lost sky and the sheet's cast are all "the
     paper is on top of this cover" — true of a shut book stored under the
     map's corner, and false the moment the hand takes it out and lays it
     OPEN on the map. The open spread is over the paper, so those three
     terms come off as it is carried, on the carry's own ease. Left on,
     they read on the leaves and not on the cloth: standing, the journal's
     white pages came out at 43 % of their colour — a dark slab on the
     sheet (measured on this branch before the fix). The FOLD (FD1,
     2026-09-20) left all three on both covers, on the argument that a
     sheet being folded stays on the covers it was lying on. That is true
     of the FIELD BOOK, which the packet lands on, and false of the
     JOURNAL: the paper over it is the east half, which is the first leaf
     to go up, and from the first degree of that turn nothing of the sheet
     is on the journal at all. Tommy, 2026-09-21: "an old pre-drawn shadow
     remains on the journal, even when it's fully folded up." So the
     journal's three terms ease out over the first fifteen degrees of the
     first turn (JS1), and come back the same way as the leaf returns; the
     field book keeps its contact through the whole fold, as before. */
  var turns = (O.foldOn && O.foldT > 0.0005 && O.foldGeom) ? O.foldGeom().a : null;
  for (var i = 0; i < 2; i++) {
    var b = bk[i];
    var opening = LIFT[2 + i].v > 0.0001;
    /* TB2: a tablet on its side lays the open book's leaf in the glass's
       middle, not on the reading station, so the block carried there would
       stand beside the page as a second book: it is lifted off the desk */
    if (TABLET && opening && S.layout && S.layout.side) continue;
    /* ==== L: THE BLOCK STAYS. Tommy: "the book underneath disappearing
       … doesn't quite add up". The shut solid is what is drawn at every
       stage: at rest under the sheet's corner, and carried (bookPose,
       carry only) to its reading place, where it stays under the panel's
       near page as the block of leaves an open book has under one side.
       The GL spread (openMesh) with its blank leaves is not drawn any
       more — the turning is the panel's own (styles.css .book-hinge).
       What is drawn is the FIXED HALF of the canonical two-width box: the
       east half for the key, the west for the journal. */
    var pose = opening ? bookPose(S, i, b, dim[i]) : null;
    var mesh = bookMesh(gl, i, dim[i]);
    /* BK1: one scale for the whole solid. The block stands in the panel's
       rectangle, so its boards, its block and the joint on its spine are
       that much thicker too — at carry 0 the scale is exactly 1 and every
       uniform below is the number the shut cover has always been given. */
    var sc = pose ? pose.scale : 1;
    var bx = pose ? (i ? pose.x : pose.x + pose.w * 0.5) : b.x, by = pose ? pose.y : b.y;
    var bw = pose ? pose.w * 0.5 : b.w, bh = pose ? pose.h : b.h;
    /* JS1: the journal's contact goes with the first leaf (see above) */
    var onCover = 1;
    if (i === 1 && turns) {
      var u1 = Math.min(1, Math.max(0, turns[0] / 0.26));
      onCover = 1 - u1 * u1 * (3 - 2 * u1);
    }
    gl.uniform3f(p.u.uPCurl, cu15, contact * (1 - (pose ? pose.carry : 0)) * onCover,
                 ctx.paper.FLOAT + ctx.paper.seTip(cu15) * 0.66);
    gl.bindVertexArray(mesh.vao); gl.uniform1f(p.u.uZpx, dim[i].z * sc * S.dpr);
    gl.uniform4f(p.u.uBox, (bx - L.ox) / L.k, (by - L.oy) / L.k, bw / L.k, bh / L.k);
    gl.uniform1f(p.u.uZ, dim[i].z * sc / L.k);
    gl.uniform1f(p.u.uZ0, pose ? pose.z / L.k : BOOK_RISE * LIFT[i].v / L.k);
    gl.uniform2f(p.u.uPx, bw, bh);
    gl.uniform2f(p.u.uSpine, dim[i].sp * sc / bw, i);
    gl.uniform1f(p.u.uFlip, i);
    gl.uniform1f(p.u.uOpen, 0);
    gl.uniform1f(p.u.uKind, i);
    if (i === 0) {          // buckram, #4d5c4a / #3d4a3c / #2f3a2f
      gl.uniform3f(p.u.uC0, 0.302, 0.361, 0.290);
      gl.uniform3f(p.u.uC1, 0.239, 0.290, 0.235);
      gl.uniform3f(p.u.uC2, 0.184, 0.227, 0.184);
    } else {                // morocco, #533226 / #3a2119 / #26130e
      gl.uniform3f(p.u.uC0, 0.325, 0.196, 0.149);
      gl.uniform3f(p.u.uC1, 0.227, 0.129, 0.098);
      gl.uniform3f(p.u.uC2, 0.149, 0.075, 0.055);
    }
    gl.drawElements(gl.TRIANGLES, mesh.n, gl.UNSIGNED_SHORT, 0);
  }
  if (!O.pSheet) ctx.depthBack(gl);
}

/* =====================================================================
   C0 — WHAT THIS PIECE TELLS THE REST OF THE DESK

   Two registrations per book and nothing else. The numbers are the ones
   the two shaders were handed by name before, arithmetic unchanged: this
   card's whole proof is that nothing moves.
   ===================================================================== */
/* ONE object per entry, mutated: the lists are evaluated on every draw
   and on every pointer move through `RoomObjects.paperHeight`, and a
   piece that allocates there allocates a garbage collection into a
   gesture. */
var UND = [{ box: [0, 0, 0, 0], top: 0 }, { box: [0, 0, 0, 0], top: 0 }];
var SHD = [{ box: [0, 0, 0, 0], height: 0, lift: 1, cast: 0.55, contact: 0.34 },
           { box: [0, 0, 0, 0], height: 0, lift: 1, cast: 0.60, contact: 0.34 }];

/* WHAT IS UNDER THE PAPER, in sheet units. A book that has GONE (its
   panel is open) takes its tent with it: the footprint draws in about
   its centre and the height under the paper goes to nought, both on the
   same ease, so the sheet settles onto the wood where the book lay
   (R67 c). `top` is the book's own thickness by how much of it is still
   there, plus R22's hover rise — its OWN number, with no reference to
   the other book, which is what the two hard-coded uniforms could not
   manage (the journal's height rode in the field book's offset). */
function underOf(i) {
  return function (ctx) {
    var S = ctx.S, L = ctx.L, u = UND[i], bx = u.box;
    /* BK2 (2026-09-21): THE TENT FALLS AS THE BOOK LEAVES, not on the hinge's
       clock. Tommy: "the way the deformation of the paper changes when you
       open the book (it goes away) doesn't fit the speed with which the
       book opening animation happens." It did not: the block is carried
       out from under the sheet on bookEase(0, 0.32, v) — done by v 0.32,
       about the first 200 ms of the 520 — while this tent drew in on the
       raw channel, so at the end of the carry the paper still stood half
       its height over bare oak (measured: value 0.51, carry 1, tent 0.49),
       and went on settling for another 300 ms with nothing under it. The
       shadow (bookGroundBox) and the block's cast (--carry-on) were on the
       carry already; now the tent is too. On the way home the same ease
       runs backwards, so the tent rises as the book arrives under the
       paper, which is what R29 meant by "the tent walks in with the
       footprint". */
    /* SO1: and the tent goes OUT WITH THE BOOK first — the footprint walks
       south as the book slides, at its full height, and only falls as the
       carry lifts the book off the oak. */
    var gv = bkGone(i), p = 1 - bkCarry(gv), z = bookDims(S)[i].z;
    var bk = NO_BOOKS ? null : O.books(S);
    if (!bk) { bx[0] = bx[1] = bx[2] = bx[3] = 0; u.top = 0; return u; }
    var b = bk[i], sdy = gv > 0 ? bkSlideDy(S, b) * bkSlide(gv) : 0;
    /* BV1 A2: on the paper, not under it; the tent rises as they go home */
    p *= O.a2Out(S);
    bx[0] = (b.x + b.w * (1 - p) / 2 - L.ox) / L.k;
    bx[1] = (b.y + sdy + b.h * (1 - p) / 2 - L.oy) / L.k;
    bx[2] = b.w * p / L.k;
    bx[3] = b.h * p / L.k;
    u.top = (z * p + BOOK_RISE * LIFT[i].v) / L.k;
    return u;
  };
}
/* WHAT STANDS ON THE OAK, in css pixels. `lift` is R22's: 1 at rest and
   (1 + rise/thickness) with a hand on the book, which is what lengthens
   and softens the throw and lets the light in under the contact line.
   The two cast weights are the ones the two named arms carried (.55 for
   the field book, .60 for the journal — the journal is thicker). */
function shadowOf(i) {
  return function (ctx) {
    var S = ctx.S, q = SHD[i], bx = q.box;
    if (NO_BOOKS) { bx[0] = bx[1] = bx[2] = bx[3] = 0; return q; }
    var bk = O.books(S), dl = bookDims(S);
    var g = bookGroundBox(S, i, bk[i], dl[i]);
    bx[0] = g.x; bx[1] = g.y; bx[2] = g.w; bx[3] = g.h;
    q.height = g.height;
    q.lift = g.height / dl[i].z + BOOK_RISE * LIFT[i].v / dl[i].z;
    return q;
  };
}

O.piece('books', drawBooks, {
  /* frame 1, because the two covers are IN the first picture: this is the
     one compile that was already on that path and it stays exactly where
     it was in O.init's order (after the E4 sheet, before the grid). */
  init: function (gl, ctx) {
    if (!NO_BOOKS) O.pBook = ctx.mk.prog(gl, BOOK_VS, BOOK_FS, 'book-e4');
  },
  place: placeBooks,
  smoke: function () {
    return { compiled: !!(O.pBook && O.pBook.u), books: NO_BOOKS ? 0 : 2,
             open: openBookIdx(), lift: [LIFT[0].v, LIFT[1].v] };
  }
});
/* the measured figures stay published where they always were, because
   scripts/test-book-physical.js and the report both read them off
   RoomObjects — the file they are declared in has changed, the fact has
   not (C0). */
O.BOOK_REAL_MM = BK_REAL_MM;
O.BOOK_ON_PAPER_MM = BOOK_ON_PAPER_MM;

O.under('book-field', underOf(0));
O.under('book-journal', underOf(1));
O.shadow('book-field', shadowOf(0), { lip: 1 });
O.shadow('book-journal', shadowOf(1), { lip: 1 });
})();
